import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { api, getToken } from '../lib/api';
import { can, canDelete, useAuth, ROLE } from '../lib/auth';
import { money, poDateText, qtyText, qtyValue } from '../lib/boardPo';
import { readNumber } from '../lib/formula';
import { NumField } from '../components/NumField';
import { ColumnFilter, type FilterOption } from '../components/ColumnFilter';
import { useDepartments, useVendors } from '../lib/lookups';
import { StorePoTab, type ReceivePreset, type StoreItemRef } from './BoardPurchaseOrders';
import { GrnForm, GrnTab } from './Grn';

/**
 * Raw Material and Spare Parts — one page, two scopes, kept as simple as the
 * Boards: one stock table in SKU order (SKU, Group, Item Name, Brand, Item
 * Code, Sub Head, Qty, Rate, Value) with + In, Issue and History on every
 * row, + Add Item, and the Purchase Order and Reports tabs.  Stock is valued
 * first in, first out; the Rate shown is the average of the batches on hand,
 * and clicking it lists them.
 *
 * Backed by /api/inventory (tblInventoryItem / tblInventoryStock /
 * tblInventoryHistory).  Stock moves only by In, Issue, a Super Admin's
 * correction or a reversal, and each is a row in the item's History.
 */

export interface Item {
  /** The item's key (ITM00001…) — stock, History and PO lines point at it; not shown. */
  itemCode: string;
  /** The SKU shown: SKU1, SKU2, … as the Boards number theirs. */
  sku?: string | null;
  /** The optional Item Code — the store's or maker's own code; blank unless typed. */
  refCode?: string | null;
  /** Where it is kept — stored, no longer shown. */
  binLocation?: string | null;
  /** Raw Material / Consumable / Plates / Blankets / Corrugated Boxes — the one thing Purchase may change. */
  subHead?: string | null;
  itemName: string;
  category: string;
  itemGroup: string | null;
  brand: string | null;
  uom: string | null;
  /** The rate, per the item's unit — the price last paid. */
  standardCost: number | null;
  isActive: boolean;
  qtyOnHand: number;
  /** What the stock on hand cost per unit — the average of its batches (null with none on hand). */
  stockRate?: number | null;
  /** The batches on hand, oldest first — the next issue takes from the first. */
  layers?: Batch[];
  /** What the stock on hand cost, first in, first out. */
  stockValue?: number;
}

/** A batch of stock on hand: what is left of one In, at the rate it came in at. */
interface Batch {
  qty: number;
  rate: number;
  /** False when it came in without a rate and is valued at the last rate known. */
  priced?: boolean;
  /** When it came in; null for stock with no History behind it. */
  at?: string | null;
  /** RECEIPT (an In), OPENING or ADJUST (a correction up). */
  type?: string | null;
  poNumber?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
}

interface HistoryRow {
  historyId: number;
  txnDate: string;
  itemCode: string;
  /** The item's SKU as shown (SKU1…). */
  sku?: string | null;
  itemName: string;
  itemGroup: string | null;
  brand: string | null;
  uom: string | null;
  txnType: string;
  qtyChange: number;
  refType: string | null;
  refId: string | null;
  remarks: string | null;
  byName: string | null;
  // A receipt's paperwork (after migration_2026_09_29_po_kinds).
  poNumber?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  rate?: number | null;
  value?: number | null;
  /** This row undoes that one. */
  reversesId: number | null;
  /** This row was undone by that one. */
  reversedById: number | null;
  /** A Store issue's check: PENDING, VERIFIED or REJECTED (null: nothing to check). */
  verifyStatus?: string | null;
  verifiedByName?: string | null;
  verifiedOn?: string | null;
  verifyNote?: string | null;
  /** The item's stock now, after every movement (this one included). */
  stockNow?: number | null;
  reorderPoint?: number | null;
  /** This user may reverse it now (Purchase: within an hour of the movement). */
  canReverse: boolean;
}

type Scope = 'raw-material' | 'spare-parts';
type Tab = 'stock' | 'out' | 'po' | 'grn' | 'reports' | 'issues';

/**
 * What each door into the page shows.  The category list decides which items
 * belong to it; new items take the default category.
 */
const SCOPES: Record<Scope, {
  title: string;
  subtitle: string;
  categories: string[];
  defaultCategory: string;
  /** Units offered on the Add Item form and on a PO line not tied to an item. */
  uoms: string[];
  poKind: 'RAW_MATERIAL' | 'SPARE_PARTS';
  /** Placeholders on the Add Item form. */
  example: { group: string; name: string; brand: string };
}> = {
  'raw-material': {
    title: 'Raw Material',
    subtitle: 'Inks, varnish, glue, foil, film and other consumables.',
    categories: ['RAW_MATERIAL', 'FOIL', 'CONSUMABLE'],
    defaultCategory: 'RAW_MATERIAL',
    uoms: ['KG', 'LTR', 'MTR', 'SQ.MTR', 'ROLL', 'PC', 'BOX', 'NOS'],
    poKind: 'RAW_MATERIAL',
    example: { group: 'e.g. AQUA VARNISH', name: 'e.g. Gloss Aqua Varnish', brand: 'e.g. Pidilite' },
  },
  'spare-parts': {
    title: 'Spare Parts',
    subtitle: 'Machine spares and tools.',
    categories: ['SPARE', 'TOOL'],
    defaultCategory: 'SPARE',
    uoms: ['PC', 'SET', 'MTR', 'NOS'],
    poKind: 'SPARE_PARTS',
    example: { group: 'e.g. BEARINGS', name: 'e.g. Bearing 6205', brand: 'e.g. SKF' },
  },
};

const TABS: { key: Tab; label: string }[] = [
  { key: 'stock', label: 'Stock' },
  { key: 'out', label: 'Out of Stock' },
  { key: 'po', label: 'Purchase Order' },
  // Raw Material only: every delivery booked in with its received date.
  { key: 'grn', label: 'GRN' },
  { key: 'reports', label: 'Reports' },
  // The Store role's issues, waiting for Purchase / Super Admin to verify.
  { key: 'issues', label: 'Store Issues' },
];
/** Links from before the page was simplified; an item's History is its row's History link. */
const TAB_ALIAS: Record<string, Tab> = { items: 'stock', procurement: 'po', history: 'stock' };

/** A stock quantity: Indian grouping, up to the 4 decimals stock is kept to. */
const fmtQ = (n: number) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 });
const q4 = (n: number) => Math.round(n * 10000) / 10000;
const unitOf = (it: { uom: string | null }) => it.uom?.trim() || '';
/** The SKU as shown (SKU1…); the item's key until it is numbered. */
const skuOf = (it: { sku?: string | null; itemCode: string }) => it.sku || it.itemCode;
const onHandOf = (it: Item) => Number(it.qtyOnHand) || 0;
/** The price last paid (set by each receipt with a rate) — the start for the next receipt. */
const lastRateOf = (it: Item) => (it.standardCost != null && Number(it.standardCost) > 0 ? Number(it.standardCost) : null);
/**
 * The rate shown: what the stock on hand cost per unit — the average of its
 * batches (1 at 169.49 and 1 at 60 is 114.75) — or the last price paid when
 * none is on hand.
 */
const rateOf = (it: Item) =>
  (onHandOf(it) > 0 && it.stockRate != null && Number(it.stockRate) > 0 ? Number(it.stockRate) : lastRateOf(it));
/** The batches on hand, when there are any to show. */
const batchesOf = (it: Item): Batch[] => (onHandOf(it) > 0 ? it.layers ?? [] : []);
/** How many different rates the stock on hand came in at — more than one makes the rate an average. */
const ratesIn = (it: Item) => new Set(batchesOf(it).map((l) => Number(l.rate))).size;
/** The batches on hand as text: "1 @ ₹169.49 · 1 @ ₹60.00" (oldest first). */
const batchesText = (it: Item) =>
  batchesOf(it).map((l) => `${fmtQ(l.qty)} @ ₹${money(l.rate)}`).join(' · ');
/** What the stock on hand cost, first in, first out, to the paisa — 1 at 169.49 and 1 at 60 is 229.49. */
function valueOf(it: Item): number {
  if (it.stockValue != null) return Number(it.stockValue) || 0;
  const r = lastRateOf(it);
  const q = onHandOf(it);
  return r != null && q > 0 ? qtyValue(q, r) : 0;
}

/** What an item is booked under — as the API keeps them. */
const SUB_HEADS = ['Raw Material', 'Consumable', 'Plates', 'Blankets', 'Corrugated Boxes'];

/** Stock-table columns with an Excel-style filter in the header. */
const FILTER_COLS = ['sku', 'itemGroup', 'itemName', 'brand', 'refCode', 'subHead'] as const;
type FilterKey = typeof FILTER_COLS[number];
const FILTER_LABEL: Record<FilterKey, string> = {
  itemGroup: 'Group', itemName: 'Item Name', brand: 'Brand', sku: 'SKU', refCode: 'Item Code', subHead: 'Sub Head',
};
/** A cell as the column filters see it: case ignored, shown as first written; '' is blank. */
function filterCell(it: Item, k: FilterKey): FilterOption {
  const s = String((k === 'sku' ? skuOf(it) : it[k]) ?? '').trim();
  return s ? { key: s.toUpperCase(), label: s } : { key: '', label: '' };
}

/** Group A→Z with items without a group last, then the item name. */
function byGroupThenName(a: Item, b: Item): number {
  const ga = (a.itemGroup ?? '').trim().toUpperCase();
  const gb = (b.itemGroup ?? '').trim().toUpperCase();
  if (ga !== gb) {
    if (!ga) return 1;
    if (!gb) return -1;
    return ga.localeCompare(gb);
  }
  return a.itemName.localeCompare(b.itemName, undefined, { sensitivity: 'base' });
}

/** The number in SKU12; none for an item not yet numbered. */
function skuNumber(sku: string | null | undefined): number | null {
  const m = /^(?:SKU|SP)(\d+)$/i.exec((sku ?? '').trim());
  return m ? Number(m[1]) : null;
}

/** SKU1, SKU2, … SKU10 (Spare Parts SP1, SP2, …) in number order; 0 when either isn't numbered (the caller decides). */
function skuOrder(a: string | null | undefined, b: string | null | undefined): number {
  const na = skuNumber(a), nb = skuNumber(b);
  if (na != null && nb != null) return na - nb;
  if (na != null || nb != null) return na != null ? -1 : 1;
  return 0;
}

/** The list's order: by SKU number; an item not yet numbered after them, by group and name. */
const bySku = (a: Item, b: Item) => skuOrder(a.sku, b.sku) || byGroupThenName(a, b);

/** The distinct values of a field, case ignored, as first written. */
function distinct(items: Item[], pick: (i: Item) => string | null): string[] {
  const seen = new Map<string, string>();
  for (const i of items) {
    const v = (pick(i) ?? '').trim();
    if (v && !seen.has(v.toUpperCase())) seen.set(v.toUpperCase(), v);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

export function InventoryPage() {
  const { user } = useAuth();
  const params = useParams<{ scope?: string; tab?: string }>();

  // Super Admin and Purchase work the stock; Accounts and GM look at it.  The
  // route checks this too; here it keeps the page from firing requests the
  // API would refuse.
  if (!can(user, 'VIEW_INVENTORY')) {
    return <div className="p-6 text-slate-500">You do not have access to the inventory.</div>;
  }
  const scope = (params.scope ?? '') as Scope;
  if (!SCOPES[scope]) return <Navigate to="/inventory/raw-material" replace />;
  const raw = params.tab ?? 'stock';
  if (TAB_ALIAS[raw]) {
    const t = TAB_ALIAS[raw];
    return <Navigate to={t === 'stock' ? `/inventory/${scope}` : `/inventory/${scope}/${t}`} replace />;
  }
  if (!TABS.some((t) => t.key === raw)) return <Navigate to={`/inventory/${scope}`} replace />;
  return <StorePage key={scope} scope={scope} tab={raw as Tab} />;
}

function StorePage({ scope, tab }: { scope: Scope; tab: Tab }) {
  const cfg = SCOPES[scope];
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  // Without it (Accounts, GM) the page is to look at and filter: no Add, In,
  // Issue or edit — the API refuses those anyway.
  const canManage = can(user, 'MANAGE_INVENTORY');
  // The Store role issues raw material (each issue then waits to be verified).
  const isStore = !canManage && can(user, 'ISSUE_INVENTORY');
  const canIssue = canManage || (isStore && scope === 'raw-material');
  const seesStoreIssues = scope === 'raw-material' && (isStore || can(user, 'VERIFY_STORE_ISSUE'));
  // Raw material comes in through a GRN; spare parts keep "+ In".
  const usesGrn = scope === 'raw-material';
  const tabs = TABS.filter((t) => (t.key === 'issues' ? seesStoreIssues
    : t.key === 'grn' ? usesGrn && !isStore
    : !(isStore && (t.key === 'po' || t.key === 'reports'))));
  const pendingQ = useQuery({
    enabled: seesStoreIssues,
    queryKey: ['inv-store-issues', 'pending', cfg.categories.join(',')],
    queryFn: () => api<{ rows: HistoryRow[] }>(`/api/inventory/history?verify=pending&categories=${encodeURIComponent(cfg.categories.join(','))}`),
  });
  const pendingCount = pendingQ.data?.rows.length ?? 0;

  const itemsQ = useQuery({
    queryKey: ['inv-items'],
    queryFn: () => api<{ rows: Item[] }>('/api/inventory/items'),
  });
  const all = useMemo(
    () => (itemsQ.data?.rows ?? []).filter((i) => cfg.categories.includes(i.category)).sort(bySku),
    [itemsQ.data, cfg],
  );
  const groups = useMemo(() => distinct(all, (i) => i.itemGroup), [all]);
  const brands = useMemo(() => distinct(all, (i) => i.brand), [all]);

  const [q, setQ] = useState('');
  const [group, setGroup] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [issuing, setIssuing] = useState<Item | null>(null);
  // A note that fades by itself after an issue - not a box to click away.
  const [notice, setNotice] = useState<string | null>(null);
  const showNotice = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice((cur) => (cur === msg ? null : cur)), 4000);
  };
  const [receiving, setReceiving] = useState<{ item: Item; preset?: ReceivePreset } | null>(null);
  const [history, setHistory] = useState<Item | null>(null);
  const [batches, setBatches] = useState<Item | null>(null);

  /** Stock, History, Reports and the POs' received counts all move together. */
  const refresh = () => qc.invalidateQueries({
    predicate: (query) => {
      const k = query.queryKey[0];
      return typeof k === 'string' && (k.startsWith('inv-') || k.startsWith('board-po'));
    },
  });
  const setTab = (t: Tab) => navigate(t === 'stock' ? `/inventory/${scope}` : `/inventory/${scope}/${t}`);

  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searched = all.filter((i) => {
    if (tab === 'out' ? onHandOf(i) > 0 : inStockOnly && onHandOf(i) <= 0) return false;
    if (group && (i.itemGroup ?? '').trim().toUpperCase() !== group.toUpperCase()) return false;
    if (!words.length) return true;
    const hay = `${i.itemGroup ?? ''} ${i.itemName} ${i.brand ?? ''} ${skuOf(i)} ${i.refCode ?? ''} ${i.subHead ?? ''} ${i.uom ?? ''}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });

  // Excel-style header filters, on top of the search / group / stock controls.
  // A column's list offers the values in rows that pass the OTHER columns'
  // filters, as Excel does.
  const [colFilters, setColFilters] = useState<Partial<Record<FilterKey, Set<string>>>>({});
  const passes = (i: Item, except?: FilterKey) =>
    FILTER_COLS.every((k) => k === except || !colFilters[k] || colFilters[k]!.has(filterCell(i, k).key));
  const shown = searched.filter((i) => passes(i));
  const optionsFor = (k: FilterKey): FilterOption[] => {
    const seen = new Map<string, string>();
    for (const i of searched) {
      if (!passes(i, k)) continue;
      const c = filterCell(i, k);
      if (!seen.has(c.key)) seen.set(c.key, c.label);
    }
    return [...seen].map(([key, label]) => ({ key, label }));
  };
  const setColFilter = (k: FilterKey, next: Set<string> | null) =>
    setColFilters((f) => {
      const n = { ...f };
      if (next) n[k] = next; else delete n[k];
      return n;
    });
  const activeFilters = FILTER_COLS.filter((k) => colFilters[k]);
  const filterBtn = (k: FilterKey) => (
    <ColumnFilter label={FILTER_LABEL[k]} options={optionsFor(k)} selected={colFilters[k] ?? null} onChange={(next) => setColFilter(k, next)} />
  );

  const outCount = all.filter((i) => onHandOf(i) <= 0).length;
  // The tiles add up what the table lists once anything narrows it.
  const narrowed = (tab === 'stock' || tab === 'out')
    && (!!words.length || !!group || inStockOnly || tab === 'out' || activeFilters.length > 0);
  const tiles = narrowed ? shown : all;
  const totalValue = tiles.reduce((p, i) => p + Math.round(valueOf(i) * 100), 0) / 100;
  // Quantity on hand, added up only once a group is picked (dropdown or the
  // Group column filter): across groups the units mean different things.
  // Items of one group in different units are totalled per unit.
  const groupPicked = !!group || !!colFilters.itemGroup;
  const qtyByUnit = new Map<string, number>();
  if (groupPicked) for (const i of tiles) qtyByUnit.set(unitOf(i), (qtyByUnit.get(unitOf(i)) ?? 0) + Math.max(0, onHandOf(i)));
  const groupQty = [...qtyByUnit].sort((a, b) => b[1] - a[1]).map(([u, q]) => `${fmtQ(q)}${u ? ` ${u}` : ''}`).join(' + ');

  /** A PO line's item, as the Receive form needs it — the full row when it is on this page. */
  const fromRef = (it: StoreItemRef): Item => all.find((i) => i.itemCode === it.code) ?? {
    itemCode: it.code, refCode: null, itemName: it.name, category: it.category, itemGroup: null, brand: it.brand ?? null,
    uom: it.uom, standardCost: it.standardCost, isActive: true, qtyOnHand: it.qtyOnHand,
  };

  return (
    <div className="p-4 md:p-6">
      {/* ── Header + tiles + actions ───────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-brand">{cfg.title}</h1>
          <p className="text-xs text-slate-500">{cfg.subtitle}</p>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <Stat value={itemsQ.data ? fmtQ(tiles.length) : '—'} label={narrowed ? 'Items · filtered' : 'Items'} tone="slate" />
          {groupPicked && itemsQ.data && <Stat value={groupQty || '0'} label="Qty on hand" tone="slate" />}
          <Stat value={itemsQ.data ? fmtQ(tiles.filter((i) => onHandOf(i) > 0).length) : '—'} label="In Stock" tone="emerald" />
          <Stat value={itemsQ.data ? fmtQ(tiles.filter((i) => onHandOf(i) <= 0).length) : '—'} label="Out of Stock" tone="red" />
          <Stat value={itemsQ.data ? `₹${money(totalValue)}` : '—'} label="Total Value" tone="amber" />
          <div className="flex items-center gap-2">
            {canManage
              ? <button className="btn-primary" onClick={() => setEditing(blankItem(cfg.defaultCategory))}>+ Add Item</button>
              : canIssue
              ? <span className="rounded bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700" title="Your issues are verified by Purchase or Super Admin">Issue only</span>
              : <span className="rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600" title="You can look and filter; stock is kept by Purchase">View only</span>}
            <button className="btn-ghost" onClick={refresh} title="Refresh">↻ Refresh</button>
          </div>
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────── */}
      <div className="flex gap-1 sm:gap-4 mb-4 border-b border-slate-200 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap ${
              tab === t.key ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'out' && outCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 text-[10px] font-semibold px-1.5 py-0.5">
                {fmtQ(outCount)}
              </span>
            )}
            {t.key === 'issues' && pendingCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 py-0.5">
                {fmtQ(pendingCount)}
              </span>
            )}
          </button>
        ))}
      </div>

      {(tab === 'stock' || tab === 'out') && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <input
              className="input flex-1 min-w-[220px]"
              placeholder="Search SKU, group, item, brand, item code or sub head"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select className="input max-w-[220px]" value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">All groups</option>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            {tab === 'stock' && (
              <label className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
                <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} />
                In stock only
              </label>
            )}
          </div>

          {/* Phones: one card per item, the actions in reach. */}
          <div className="md:hidden space-y-2">
            {itemsQ.isLoading ? (
              <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
            ) : itemsQ.isError ? (
              <div className="p-6 text-center text-sm text-red-600">Could not load the items.</div>
            ) : shown.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                {all.length === 0 ? (canManage ? 'No items yet — use + Add Item.' : 'No items yet.') : tab === 'out' ? 'Nothing is out of stock.' : 'No items match.'}
              </div>
            ) : shown.map((it) => {
              const qty = onHandOf(it);
              const unit = unitOf(it);
              const rate = rateOf(it);
              return (
                <div key={it.itemCode} className={`rounded-lg border bg-white p-3 shadow-sm ${qty <= 0 ? 'border-red-200' : 'border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase text-slate-500">
                        <span className="font-mono font-bold text-brand">{skuOf(it)}</span> · {it.itemGroup || '—'}
                      </div>
                      <div className="font-semibold text-slate-800">{it.itemName}</div>
                      <div className="text-xs text-slate-500">
                        {it.brand || '—'}
                        {it.refCode && <> · Code <span className="font-mono">{it.refCode}</span></>}
                        {it.subHead && <> · {it.subHead}</>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-lg font-bold leading-tight ${qty <= 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                        {fmtQ(qty)}{unit && <span className="text-xs font-normal text-slate-500"> {unit}</span>}
                      </div>
                      {rate != null && (
                        <div className="text-[11px] text-slate-500">
                          <RateText item={it} onOpen={() => setBatches(it)} compact /> · <span className="font-semibold text-amber-700">₹{money(valueOf(it))}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-5 border-t border-slate-100 pt-2 text-sm font-bold">
                    {canManage && <button className="text-emerald-700" onClick={() => setReceiving({ item: it })}>+ In</button>}
                    {canIssue && <button className="text-brand disabled:text-slate-300" onClick={() => setIssuing(it)} disabled={qty <= 0}>Issue</button>}
                    <button className="text-slate-700" onClick={() => setHistory(it)}>History</button>
                    {canManage && <button className="ml-auto font-medium text-slate-500" onClick={() => setEditing(it)}>edit</button>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden md:block bg-white rounded shadow overflow-x-auto">
            {/* Ten columns: kept tight so Value fits beside the sidebar on a laptop. */}
            <table className="w-full text-xs [&_td]:px-2 [&_th]:px-2">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>SKU{filterBtn('sku')}</Th>
                  <Th>Group{filterBtn('itemGroup')}</Th>
                  <Th>Item Name{filterBtn('itemName')}</Th>
                  <Th>Brand{filterBtn('brand')}</Th>
                  <Th>Item Code{filterBtn('refCode')}</Th>
                  <Th>Sub Head{filterBtn('subHead')}</Th>
                  <Th numeric>Qty on Hand</Th>
                  <Th>Action</Th>
                  <Th numeric>Rate (₹)</Th>
                  <Th numeric>Value (₹)</Th>
                </tr>
              </thead>
              <tbody>
                {itemsQ.isLoading ? (
                  <tr><td colSpan={10} className="p-6 text-center text-slate-400">Loading…</td></tr>
                ) : itemsQ.isError ? (
                  <tr><td colSpan={10} className="p-6 text-center text-red-600">Could not load the items.</td></tr>
                ) : shown.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-6 text-center text-slate-400">
                      {all.length === 0 ? (canManage ? 'No items yet — use + Add Item.' : 'No items yet.') : tab === 'out' ? 'Nothing is out of stock.' : 'No items match.'}
                    </td>
                  </tr>
                ) : shown.map((it) => {
                  const qty = onHandOf(it);
                  const unit = unitOf(it);
                  const rate = rateOf(it);
                  return (
                    <tr key={it.itemCode} className={`border-t border-slate-100 hover:bg-yellow-100 text-slate-800 ${qty <= 0 ? 'bg-red-50/40' : ''}`}>
                      <td className="px-3 py-1.5 font-mono text-[11px] font-bold text-brand whitespace-nowrap">{skuOf(it)}</td>
                      <td className="px-3 py-1.5 font-semibold text-slate-600">{it.itemGroup || '—'}</td>
                      <td className="px-3 py-1.5 font-semibold">
                        {it.itemName}
                        {!it.isActive && <span className="ml-1.5 rounded bg-slate-100 px-1 text-[10px] font-normal text-slate-500">inactive</span>}
                      </td>
                      <td className="px-3 py-1.5">{it.brand || '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{it.refCode || '—'}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{it.subHead || '—'}</td>
                      <td className={`px-3 py-1.5 text-right font-bold whitespace-nowrap ${qty <= 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                        {fmtQ(qty)}{unit && <span className="font-normal text-slate-500"> {unit}</span>}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap font-bold">
                        {canManage && (
                          <button className="text-emerald-700 hover:underline mr-2" title="Stock coming in" onClick={() => setReceiving({ item: it })}>+ In</button>
                        )}
                        {canIssue && (
                          <>
                            <button
                              className="text-brand hover:underline mr-2 disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
                              title="Issue stock out"
                              onClick={() => setIssuing(it)}
                              disabled={qty <= 0}
                            >Issue</button>
                          </>
                        )}
                        <button className="text-slate-700 hover:text-brand hover:underline mr-2" title="Every movement of this item" onClick={() => setHistory(it)}>History</button>
                        {canManage && <button className="text-slate-500 hover:text-brand hover:underline font-medium" onClick={() => setEditing(it)}>edit</button>}
                      </td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">
                        <RateText item={it} onOpen={() => setBatches(it)} />
                      </td>
                      <td className="px-3 py-1.5 text-right font-bold text-amber-700">{rate != null ? money(valueOf(it)) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-400 mt-2">
            {shown.length} item(s) shown
            {activeFilters.length > 0 && (
              <>
                {' '}· filtered by {activeFilters.map((k) => FILTER_LABEL[k]).join(', ')}
                {' '}·{' '}
                <button type="button" className="text-brand underline hover:text-brand-700" onClick={() => setColFilters({})}>
                  Clear all filters
                </button>
              </>
            )}
          </div>
        </>
      )}

      {tab === 'po' && !isStore && (
        <>
          <StorePoTab
            kind={cfg.poKind}
            categories={cfg.categories}
            uoms={cfg.uoms}
            onReceive={(it, preset) => setReceiving({ item: fromRef(it), preset })}
          />
          <LegacyPoList categories={cfg.categories} />
        </>
      )}
      {tab === 'grn' && usesGrn && !isStore && <GrnTab kind="ITEM" categories={cfg.categories} />}
      {tab === 'reports' && !isStore && <ReportsTab categories={cfg.categories} />}
      {tab === 'po' && isStore && <p className="text-sm text-slate-500">Purchase orders are kept by Purchase.</p>}
      {tab === 'issues' && seesStoreIssues && <StoreIssuesTab categories={cfg.categories} onChanged={refresh} />}
      {tab === 'issues' && !seesStoreIssues && <p className="text-sm text-slate-500">Store issues are on the Raw Material page.</p>}

      {editing && (
        <ItemEditor
          scope={scope}
          item={editing}
          groups={groups}
          brands={brands}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {issuing && (
        <IssueModal item={issuing} onClose={() => setIssuing(null)} onSaved={() => {
          setIssuing(null);
          refresh();
          showNotice(isStore ? `Issued ${issuing.itemName} — waiting for Purchase or Super Admin to verify.` : `Issued ${issuing.itemName}.`);
        }} />
      )}
      {receiving && usesGrn && (
        <GrnForm
          kind="ITEM"
          categories={cfg.categories}
          start={{ itemCode: receiving.item.itemCode, poNumber: receiving.preset?.poNumber, qty: receiving.preset?.qty, rate: receiving.preset?.rate }}
          onClose={() => setReceiving(null)}
          onSaved={refresh}
        />
      )}
      {receiving && !usesGrn && (
        <ReceiveModal
          item={receiving.item}
          preset={receiving.preset}
          onClose={() => setReceiving(null)}
          onSaved={() => { setReceiving(null); refresh(); }}
        />
      )}
      {history && <HistoryModal item={history} onClose={() => setHistory(null)} onChanged={refresh} />}
      {batches && <BatchesModal item={batches} onClose={() => setBatches(null)} />}
      {notice && (
        <div role="status" className="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg bg-emerald-600 px-4 py-3 text-sm font-medium text-white shadow-lg">
          ✓ {notice}
        </div>
      )}
    </div>
  );
}

const blankItem = (category: string): Item => ({
  itemCode: '', refCode: null, itemName: '', category, itemGroup: null, brand: null, uom: null,
  standardCost: null, isActive: true, qtyOnHand: 0,
});

function Th({ children, numeric }: { children: ReactNode; numeric?: boolean }) {
  return (
    <th className={`px-3 py-2 font-medium text-slate-600 whitespace-nowrap ${numeric ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

/**
 * The rate as the list shows it.  With stock on hand it is what that stock
 * cost per unit and opens the batches behind it; "avg of 2 rates" marks one
 * that is an average.  With none on hand it is the last price paid.
 */
function RateText({ item, onOpen, compact }: {
  item: Item;
  onOpen: () => void;
  /** Phones: ₹ in front and the avg note on the same line. */
  compact?: boolean;
}) {
  const rate = rateOf(item);
  const unit = unitOf(item);
  if (rate == null) return <span className="text-slate-300">—</span>;
  const shown = <>{compact && '₹'}{money(rate)}{unit && <span className="text-slate-400">/{unit}</span>}</>;
  if (!batchesOf(item).length) return <span title="Last price paid (none on hand)">{shown}</span>;
  const rates = ratesIn(item);
  return (
    <button
      type="button"
      className="text-right hover:text-brand"
      title={`What came in at which rate: ${batchesText(item)}`}
      onClick={onOpen}
    >
      <span className="underline decoration-dotted underline-offset-2">{shown}</span>
      {rates > 1 && (compact
        ? <span className="ml-1 text-[10px] font-semibold text-amber-700">avg ▸</span>
        : <span className="block text-[10px] font-semibold text-amber-700">avg of {rates} rates ▸</span>)}
    </button>
  );
}

function Stat({ value, label, tone }: { value: string; label: string; tone: 'slate' | 'emerald' | 'red' | 'amber' }) {
  const colour = { slate: 'text-slate-800', emerald: 'text-emerald-600', red: 'text-red-600', amber: 'text-amber-600' }[tone];
  return (
    <div className="flex flex-col items-end leading-none">
      <div className={`text-lg font-bold ${colour}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

/** The item as the forms head it: name · brand · group · code. */
function ItemLine({ item }: { item: Item }) {
  return (
    <>
      <span className="font-semibold text-slate-800">{item.itemName}</span>
      {item.brand && <> · {item.brand}</>}
      {item.itemGroup && <> · {item.itemGroup}</>}
      <span className="text-slate-400"> · {skuOf(item)}</span>
    </>
  );
}

/** The first number that can't be used, as a message; null when all are fine. */
function badNumber(fields: [string, number | null][]): string | null {
  const bad = fields.find(([, v]) => v != null && (Number.isNaN(v) || v < 0));
  if (!bad) return null;
  return `${bad[0]}: ${Number.isNaN(bad[1]) ? "can't read that — use a number or =formula" : "can't be negative"}.`;
}

// ---------------------------------------------------------------- Add / edit

function itemError(m: string, isNew: boolean): string {
  if (m.startsWith('duplicate_item:')) {
    const code = m.slice('duplicate_item:'.length);
    return isNew
      ? `This item is already ${code} (same group, name and brand). Use + In on ${code} instead of adding it again.`
      : `${code} is already this item (same group, name and brand).`;
  }
  switch (m) {
    case 'item_code_exists': return 'That SKU is already used.';
    case 'item_has_stock':   return 'Issue the stock (or correct it to 0) before deleting the item.';
    case 'item_not_found':   return 'This item is no longer there — refresh the page.';
    case 'validation_failed': return 'Some fields are invalid — check the name, rate and quantity.';
    default: return m;
  }
}

/**
 * Add an item, or edit one.  A new item takes the next SKU (SKU1, SKU2, …);
 * its Item Code is optional.  A new item's quantity is its opening stock (the
 * first row of its History).  On edit, Purchase changes only the Sub Head —
 * the rest is Super Admin's, and a stock changed here is saved as a
 * correction in History.
 */
function ItemEditor({ scope, item, groups, brands, onClose, onSaved }: {
  scope: Scope;
  item: Item;
  groups: string[];
  brands: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const cfg = SCOPES[scope];
  const { user } = useAuth();
  const isNew = !item.itemCode;
  const full = isNew || can(user, 'EDIT_MASTERS');
  // Purchase may also set a Raw Material item's Group and Item Code (the API allows the same).
  const groupEditable = full || (scope === 'raw-material' && can(user, 'MANAGE_INVENTORY'));
  const onHand = onHandOf(item);

  const [group, setGroup] = useState(item.itemGroup ?? '');
  const [name, setName] = useState(item.itemName);
  const [brand, setBrand] = useState(item.brand ?? '');
  const [refCode, setRefCode] = useState(item.refCode ?? '');
  const [subHead, setSubHead] = useState(item.subHead ?? '');
  const [unit, setUnit] = useState(item.uom ?? '');
  const [rateText, setRateText] = useState(lastRateOf(item) != null ? String(lastRateOf(item)) : '');
  const [qtyInput, setQtyInput] = useState(isNew ? '' : String(onHand));
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const rate = readNumber(rateText);
  const qty = readNumber(qtyInput);
  const qtyOk = qty != null && !Number.isNaN(qty) && qty >= 0;
  const stockChanged = !isNew && full && qtyOk && Math.abs(q4(qty) - onHand) > 1e-9;
  // A new item's opening stock is worth qty x its rate; an item already in
  // stock is worth what its batches cost (a correction moves at that rate too).
  const avg = !isNew && item.stockRate != null && Number(item.stockRate) > 0 ? Number(item.stockRate) : null;
  const value = isNew
    ? (qtyOk && qty > 0 && rate != null && rate > 0 ? qtyValue(qty, rate) : null)
    : stockChanged
      ? (qty! > 0 && (avg ?? rate) != null ? qtyValue(qty!, (avg ?? rate)!) : 0)
      : valueOf(item);
  const shownUnit = unit.trim().toUpperCase();

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(
      isNew ? '/api/inventory/items' : `/api/inventory/items/${encodeURIComponent(item.itemCode)}`,
      { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(body) },
    ),
    onSuccess: onSaved,
    onError: (e) => setErr(itemError((e as Error).message, isNew)),
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/inventory/items/${encodeURIComponent(item.itemCode)}`, { method: 'DELETE' }),
    onSuccess: onSaved,
    onError: (e) => setErr(itemError((e as Error).message, false)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) { setErr('Item Name is required.'); return; }
    if (full) {
      const problem = badNumber([['Rate', rate], [isNew ? 'Qty' : 'Qty on hand', qty]]);
      if (problem) { setErr(problem); return; }
    }
    const text = (s: string) => s.trim() || null;
    if (isNew) {
      save.mutate({
        refCode: text(refCode),
        subHead: text(subHead),
        itemName: name.trim(),
        category: cfg.defaultCategory,
        itemGroup: text(group),
        brand: text(brand),
        uom: text(unit),
        standardCost: rate ?? null,
        openingQty: qty != null ? q4(qty) : 0,
      });
      return;
    }
    if (stockChanged && !confirm(`Set the stock of ${item.itemName} from ${fmtQ(onHand)} to ${fmtQ(q4(qty!))}${shownUnit ? ` ${shownUnit}` : ''}?\n\nThe difference is saved in its History as a correction.`)) return;
    // Purchase sends the Sub Head (and, on Raw Material, the Group and Item
    // Code) — the API keeps the rest as it is anyway.
    save.mutate(!full ? { subHead: text(subHead), ...(groupEditable ? { itemGroup: text(group), refCode: text(refCode) } : {}) } : {
      subHead: text(subHead),
      itemName: name.trim(),
      itemGroup: text(group),
      brand: text(brand),
      refCode: text(refCode),
      ...(full ? {
        uom: text(unit),
        standardCost: rate ?? null,
        ...(stockChanged ? { stockQty: q4(qty!), stockNote: text(note) } : {}),
      } : {}),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h2 className="text-lg font-semibold mb-4">{isNew ? 'Add item' : `Edit ${skuOf(item)}`}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Group</label>
            <input className="input disabled:bg-slate-50 disabled:text-slate-500" list="inv-groups" value={group} onChange={(e) => setGroup(e.target.value)} placeholder={cfg.example.group} autoFocus={isNew} disabled={!groupEditable} />
            <datalist id="inv-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
          </div>
          <div>
            <label className="label">Item Name *</label>
            <input className="input disabled:bg-slate-50 disabled:text-slate-500" value={name} onChange={(e) => setName(e.target.value)} placeholder={cfg.example.name} disabled={!full} />
          </div>
          <div>
            <label className="label">Brand</label>
            <input className="input disabled:bg-slate-50 disabled:text-slate-500" list="inv-brands" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={cfg.example.brand} disabled={!full} />
            <datalist id="inv-brands">{brands.map((b) => <option key={b} value={b} />)}</datalist>
          </div>
          <div>
            <label className="label">SKU</label>
            <input className="input bg-slate-50 text-slate-500 font-mono" value={isNew ? 'Auto — next SKU number' : skuOf(item)} disabled />
          </div>
          <div>
            <label className="label">Item Code <span className="font-normal text-slate-400">(optional)</span></label>
            <input className="input font-mono disabled:bg-slate-50 disabled:text-slate-500" value={refCode} onChange={(e) => setRefCode(e.target.value)} placeholder="The maker's or store's code, e.g. C96002" disabled={!groupEditable} />
          </div>
          <div>
            <label className="label">Sub Head</label>
            <select className="input" value={subHead} onChange={(e) => setSubHead(e.target.value)} autoFocus={!isNew && !full}>
              <option value="">— Select —</option>
              {/* A value from before the list, kept until it is changed. */}
              {subHead && !SUB_HEADS.includes(subHead) && <option value={subHead}>{subHead}</option>}
              {SUB_HEADS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Unit</label>
            <input className="input disabled:bg-slate-50 disabled:text-slate-500" list="inv-units" value={unit}
              onChange={(e) => setUnit(e.target.value)} placeholder="KG / LTR / PC …" disabled={!full} />
            <datalist id="inv-units">{cfg.uoms.map((u) => <option key={u} value={u} />)}</datalist>
          </div>
          <NumField label={isNew ? `Rate (₹ per ${shownUnit || 'unit'})` : `Last rate paid (₹ per ${shownUnit || 'unit'})`}
            text={rateText} onText={setRateText} value={rate}
            placeholder="e.g. 450 or =formula" digits={4} disabled={!full} />
          <NumField
            label={isNew ? `Qty${shownUnit ? ` (${shownUnit})` : ''} — opening stock` : `Qty on hand${shownUnit ? ` (${shownUnit})` : ''}`}
            text={qtyInput} onText={setQtyInput} value={qty}
            placeholder={isNew ? 'Stock on the shelf now, e.g. 25' : ''} digits={4} disabled={!full}
          />
          <div>
            <label className="label">{isNew ? 'Value (Qty x Rate)' : 'Value (first in, first out)'}</label>
            <input className="input bg-slate-50" readOnly tabIndex={-1} value={value != null ? `₹ ${money(value)}` : ''} />
            {!isNew && !!item.layers?.length && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                On hand, oldest first: {batchesText(item)}{shownUnit && ` ${shownUnit}`} — the next issue takes the oldest.
              </div>
            )}
          </div>
          {stockChanged && (
            <div className="sm:col-span-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-2">
              <div>
                Stock: {fmtQ(onHand)} → <b className="text-sm">{fmtQ(q4(qty!))}</b>{shownUnit && ` ${shownUnit}`}. Saved as a correction in History.
              </div>
              <input className="input bg-white" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional), e.g. physical count" />
            </div>
          )}
          {!isNew && !full && (
            <div className="sm:col-span-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              You can change the Sub Head. The rest is set by Super Admin — + In and Issue move the stock.
            </div>
          )}
          {!isNew && full && !stockChanged && (
            <div className="sm:col-span-2 text-[11px] text-slate-500">
              + In and Issue move the stock. Change Qty on hand here only to correct it to what is on the shelf.
            </div>
          )}
        </div>

        {err && <div className="mt-3 text-sm text-red-700">{err}</div>}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {!isNew && canDelete(user) && (
            <button
              type="button"
              className="mr-auto text-sm text-red-600 hover:underline disabled:text-slate-300 disabled:no-underline"
              disabled={remove.isPending || onHand > 0}
              title={onHand > 0 ? 'Only an item with no stock can be deleted' : 'Delete this item'}
              onClick={() => { if (confirm(`Delete ${item.itemName} (${skuOf(item)})? Its History stays.`)) remove.mutate(); }}
            >
              Delete item
              {/* Said, not only on hover: a disabled button gives no reason. */}
              {onHand > 0 && (
                <span className="ml-2 font-normal text-slate-500 no-underline">
                  — {fmtQ(onHand)}{shownUnit ? ` ${shownUnit}` : ''} still on hand: issue it, or set Qty on hand to 0, then delete
                </span>
              )}
            </button>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : isNew ? 'Add item' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- Issue / In

function txnError(m: string, unit: string): string {
  if (m.startsWith('insufficient_stock')) {
    const have = Number(m.split(':')[1]);
    return Number.isFinite(have) ? `Only ${fmtQ(have)}${unit ? ` ${unit}` : ''} on hand.` : 'Not enough stock on hand.';
  }
  switch (m) {
    case 'item_not_found':    return 'This item is no longer there — refresh the page.';
    case 'job_required':      return 'Enter the job card number.';
    case 'issued_to_required': return 'Say where it is issued to: a department, a person or a job card.';
    case 'job_not_found':     return 'No job card with that number.';
    case 'validation_failed': return 'Some fields are invalid — check the quantity, rate and invoice date.';
    case 'raw_material_only': return 'The Store issues raw material only.';
    case 'issue_only':        return 'The Store can only issue.';
    case 'store_role_migration_pending': return 'This needs the latest database update — run the store role migration.';
    default: return m;
  }
}

/** Issue stock out: how much, to what (a department picked from the master, a person or a job card), and why. */
/** A job card as the Issue form shows it (GET /api/paper-stock/job-info/:jobId). */
interface JobInfo {
  found: boolean; jobId?: string; clientName?: string; productName?: string; poNumber?: string; jobCardQty?: number;
  /** The job card's sheet cut size ("18.5 x 24.25") and its sheets with wastage. */
  cutSize?: string; sheetsPlanned?: number;
}

/**
 * Lamination film used for a job, in kg:  L x W (inch) x F / 3100 / 500 x sheets.
 * F is the film's weight in g/m2 (3100 x 500 = 1,550,000 turns in2 x g/m2
 * into kg): BOPP gloss 10, BOPP matt 11, Met PET 16.  Other films (thermal,
 * velvet, holographic ...) have no number yet and are issued by weight.
 */
function laminationFilm(item: Item): { f: number; label: string } | null {
  const name = (item.itemName ?? '').toUpperCase();
  const isFilm = (item.itemGroup ?? '').toUpperCase().includes('LAMINATION') || /\bFILM\b/.test(name);
  if (!isFilm) return null;
  if (/MET\s*PET/.test(name)) return { f: 16, label: 'Met PET' };
  if (/THERMAL/.test(name)) return null;
  if (/MATT/.test(name)) return { f: 11, label: 'Matt' };
  if (/GLOSS/.test(name)) return { f: 10, label: 'Gloss' };
  return null;
}
/** A job card's cut size side in inches: sizes over 60 are in mm. */
const toInch = (v: number) => (v > 60 ? Math.round((v / 25.4) * 100) / 100 : v);

function IssueModal({ item, onClose, onSaved }: { item: Item; onClose: () => void; onSaved: () => void }) {
  const unit = unitOf(item);
  const onHand = onHandOf(item);
  const [qtyInput, setQtyInput] = useState('');
  // Three ways to issue: to a department (picked from the department master),
  // to a person (free text), or against a job card.  (Machines within the
  // department are not asked for, for now.)
  const [mode, setMode] = useState<'dept' | 'person' | 'job'>('dept');
  const departments = useDepartments();
  const [dept, setDept] = useState('');
  const [issuedTo, setIssuedTo] = useState('');
  const [jobNo, setJobNo] = useState('');
  const [job, setJob] = useState<{ asked: string; info: JobInfo | null; checking: boolean }>({ asked: '', info: null, checking: false });
  const [remarks, setRemarks] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const qty = readNumber(qtyInput);

  // Lamination film: the kg from the sheet size and sheets (filled from the
  // job card when issuing to one); it fills the quantity, which can be changed.
  const film = laminationFilm(item);
  const [lam, setLam] = useState({ l: '', w: '', sheets: '' });
  const lamKg = (v: typeof lam) => {
    const L = Number(v.l), W = Number(v.w), sh = Number(v.sheets);
    return film && L > 0 && W > 0 && sh > 0 ? Math.round(((L * W * film.f) / 3100 / 500) * sh * 100) / 100 : null;
  };
  const setLamField = (patch: Partial<typeof lam>) => {
    const next = { ...lam, ...patch };
    // Nothing changed (e.g. the job card had nothing new): leave a kg typed by hand.
    if (next.l === lam.l && next.w === lam.w && next.sheets === lam.sheets) return;
    setLam(next);
    const kg = lamKg(next);
    if (kg != null) setQtyInput(String(kg));
    setErr(null);
  };
  const lamNow = lamKg(lam);
  /** From a job card: its cut size and sheets, where the store has not typed them. */
  const fillLamFromJob = (info: JobInfo | null) => {
    if (!film || !info?.found) return;
    const [l, w] = (info.cutSize ?? '').split(/\s*[x×X]\s*/).map(Number);
    setLamField({
      l: lam.l || (l && l > 0 ? String(toInch(l)) : ''),
      w: lam.w || (w && w > 0 ? String(toInch(w)) : ''),
      sheets: lam.sheets || (info.sheetsPlanned ? String(info.sheetsPlanned) : ''),
    });
  };

  /** Look the job card up (81196 or VP/082026/81196); null when the number is empty. */
  async function checkJob(raw: string): Promise<JobInfo | null> {
    const no = raw.trim();
    if (!no) { setJob({ asked: '', info: null, checking: false }); return null; }
    if (job.asked === no && job.info) return job.info;
    setJob({ asked: no, info: null, checking: true });
    try {
      const info = await api<JobInfo>(`/api/paper-stock/job-info/${encodeURIComponent(no)}`);
      setJob({ asked: no, info, checking: false });
      fillLamFromJob(info);
      return info;
    } catch {
      const info = { found: false };
      setJob({ asked: no, info, checking: false });
      return info;
    }
  }

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/inventory/txns', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: onSaved,
    onError: (e) => setErr(txnError((e as Error).message, unit)),
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const problem = badNumber([['Quantity', qty]]);
    if (problem) { setErr(problem); return; }
    const q = qty != null ? q4(qty) : 0;
    if (!(q > 0)) { setErr(`Enter the quantity issued${unit ? ` (${unit})` : ''}.`); return; }
    if (q > onHand + 1e-9) { setErr(`Only ${fmtQ(onHand)}${unit ? ` ${unit}` : ''} on hand.`); return; }
    // Every issue says where it went.
    if (mode === 'person' && !issuedTo.trim()) { setErr("Enter the person's name."); return; }
    let ref: { refType: string | null; refId: string | null } = { refType: 'ISSUED_TO', refId: issuedTo.trim() };
    if (mode === 'dept') {
      if (!dept) { setErr('Pick the department.'); return; }
      // Kept by name, as History and the reports show it.
      ref = { refType: 'DEPT', refId: dept };
    }
    if (mode === 'job') {
      if (!jobNo.trim()) { setErr('Enter the job card number.'); return; }
      const info = await checkJob(jobNo);
      if (!info?.found || !info.jobId) { setErr(`No job card ${jobNo.trim()}.`); return; }
      ref = { refType: 'JOB_CARD', refId: info.jobId };
    }
    // How the film was worked out stays with the issue, for anyone checking it.
    const lamNote = film && lamNow != null
      ? `Lamination ${lam.l} x ${lam.w} in x ${Number(lam.sheets).toLocaleString('en-IN')} sheets (${film.label} ${film.f}) = ${lamNow} kg`
      : '';
    save.mutate({
      itemCode: item.itemCode,
      txnType: 'ISSUE',
      qty: q,
      ...ref,
      remarks: [remarks.trim(), lamNote].filter(Boolean).join(' | ').slice(0, 500) || null,
      ...(film && lamNow != null
        ? { formula: { lengthIn: Number(lam.l), widthIn: Number(lam.w), sheets: Math.round(Number(lam.sheets)) } }
        : {}),
    });
  }

  const tab = (m: 'dept' | 'person' | 'job', label: string) => (
    <button type="button" onClick={() => { setMode(m); setErr(null); }}
      className={`flex-1 rounded px-3 py-1.5 text-sm font-medium ${mode === m ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}>
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <h2 className="text-lg font-semibold mb-1 text-brand">Issue</h2>
        <div className="text-xs text-slate-500 mb-4">
          <ItemLine item={item} />
          <div className="mt-0.5">On hand: <b className="text-emerald-700">{fmtQ(onHand)}</b>{unit && ` ${unit}`}</div>
        </div>
        <div className="grid gap-3">
          {film && (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
              <div className="mb-2 text-xs font-semibold text-violet-900">
                Lamination film — {film.label} ({film.f} g/m²)
              </div>
              <div className="grid grid-cols-3 gap-2">
                {([['l', 'Sheet L (inch)'], ['w', 'Sheet W (inch)'], ['sheets', 'Sheets']] as const).map(([k, label]) => (
                  <div key={k}>
                    <label className="text-[11px] font-medium text-violet-900">{label}</label>
                    <input className="input !py-1.5" inputMode="decimal" value={lam[k]}
                      onChange={(e) => setLamField({ [k]: e.target.value.replace(/[^0-9.]/g, '') })} />
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-violet-900">
                {lamNow != null
                  ? <>{lam.l} × {lam.w} × {film.f} ÷ 3100 ÷ 500 × {Number(lam.sheets).toLocaleString('en-IN')} = <b>{lamNow.toLocaleString('en-IN')} kg</b> — filled in below; you can change it.</>
                  : <>L × W × {film.f} ÷ 3100 ÷ 500 × sheets. {mode === 'job' ? 'Filled from the job card once it is found.' : 'Fill all three to work out the kg.'}</>}
              </div>
            </div>
          )}
          <NumField label={`Quantity${unit ? ` (${unit})` : ''} *`} text={qtyInput} onText={(t) => { setQtyInput(t); setErr(null); }} value={qty}
            placeholder="=formula OK" digits={4} autoFocus />
          <div>
            <label className="label">Issue to *</label>
            <div className="flex gap-1 rounded-lg bg-slate-100 p-1 mb-2">
              {tab('dept', 'Department')}
              {tab('person', 'Person')}
              {tab('job', 'Job card')}
            </div>
            {mode === 'dept' ? (
              <>
                <select className="input" value={dept} onChange={(e) => { setDept(e.target.value); setErr(null); }}>
                  <option value="">{departments.isLoading ? 'Loading…' : '— Pick the department —'}</option>
                  {(departments.data ?? []).map((d) => <option key={d.id} value={d.name.trim()}>{d.name.trim()}</option>)}
                </select>
                {departments.isError && <div className="mt-1 text-xs text-red-700">Could not load the departments — use Person and type the name.</div>}
              </>
            ) : mode === 'person' ? (
              <input className="input" value={issuedTo} onChange={(e) => { setIssuedTo(e.target.value); setErr(null); }} maxLength={100}
                placeholder="Person's name" />
            ) : (
              <>
                <input className="input" value={jobNo} autoFocus maxLength={100}
                  onChange={(e) => { setJobNo(e.target.value); setErr(null); }}
                  onBlur={() => { void checkJob(jobNo); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !job.info) { e.preventDefault(); void checkJob(jobNo); } }}
                  placeholder="Job card no. — e.g. 81196 or VP/082026/81196" />
                {job.checking && <div className="mt-1 text-xs text-slate-500">Looking up the job card…</div>}
                {!job.checking && job.info && job.asked === jobNo.trim() && (
                  job.info.found ? (
                    <div className="mt-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
                      <b className="font-mono">{job.info.jobId}</b>
                      {job.info.clientName && <> · {job.info.clientName}</>}
                      {job.info.productName && <div>{job.info.productName}</div>}
                      {job.info.poNumber && <div className="text-emerald-700">PO {job.info.poNumber}</div>}
                      {/* What the store measures the film or material against. */}
                      <div className="mt-1 grid grid-cols-2 gap-2 border-t border-emerald-200 pt-1">
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-emerald-700">Sheet cut size</div>
                          <div className="font-semibold">{job.info.cutSize || '—'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-emerald-700">Sheets with wastage</div>
                          <div className="font-semibold">{job.info.sheetsPlanned ? job.info.sheetsPlanned.toLocaleString('en-IN') : '—'}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-red-700">No job card {jobNo.trim()}.</div>
                  )
                )}
              </>
            )}
          </div>
          <div>
            <label className="label">Remarks</label>
            <input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)} maxLength={500} placeholder="Optional" />
          </div>
        </div>
        {err && <div className="text-red-700 text-sm mt-3">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Issue'}</button>
        </div>
      </form>
    </div>
  );
}

/** An open raw material / spare parts PO that includes the item being received. */
interface OpenItemPo {
  poId: number;
  poNumber: string;
  poDate: string;
  vendorName: string;
  qty: number;
  uom: string;
  rate: number;
  receivedQty: number;
}

/**
 * Stock coming in, with its paperwork — the PO it came against, the
 * supplier's invoice number and date, the rate and its value — as on the
 * board's Receive form.  A receipt quoting a raw material / spare parts PO
 * counts as received on that PO, and its rate becomes the item's rate.
 */
function ReceiveModal({ item, preset, onClose, onSaved }: {
  item: Item;
  /** Opened from a PO line: that PO, the quantity still due and the PO rate. */
  preset?: ReceivePreset;
  onClose: () => void;
  onSaved: () => void;
}) {
  const unit = unitOf(item);
  const [qtyInput, setQtyInput] = useState(preset?.qty ? String(preset.qty) : '');
  const [rateText, setRateText] = useState(() =>
    preset?.rate != null && preset.rate > 0 ? String(preset.rate)
      // Otherwise the item's rate — the price last paid — as a starting point.
      : lastRateOf(item) != null ? String(lastRateOf(item)) : '');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [poNumber, setPoNumber] = useState(preset?.poNumber ?? '');
  // Who it came from: picked from the vendor master, or typed.
  const [vendorName, setVendorName] = useState('');
  const vendors = useVendors();
  const [remarks, setRemarks] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // Open POs that include this item, so a delivery can be booked against the
  // right one (the PO then shows it as received).
  const openPos = useQuery({
    queryKey: ['board-po-open-for-item', item.itemCode],
    queryFn: () => api<{ rows: OpenItemPo[] }>(`/api/board-po/open-for-item/${encodeURIComponent(item.itemCode)}`),
    retry: false,
  });
  const pos = openPos.data?.rows ?? [];
  function pickPo(n: string) {
    setPoNumber(n);
    const po = pos.find((p) => p.poNumber === n);
    if (!po) return;
    if (po.vendorName?.trim()) setVendorName(po.vendorName.trim());
    const due = Math.max(0, po.qty - po.receivedQty);
    if (!qtyInput.trim() && due) setQtyInput(String(due));
    if (po.rate > 0) setRateText(String(po.rate));
  }

  const qty = readNumber(qtyInput);
  const rate = readNumber(rateText);
  const value = qty != null && qty > 0 && rate != null && rate > 0 ? qtyValue(qty, rate) : null;

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/inventory/txns', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: onSaved,
    onError: (e) => setErr(txnError((e as Error).message, unit)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const problem = badNumber([['Quantity', qty], ['Rate', rate]]);
    if (problem) { setErr(problem); return; }
    const q = qty != null ? q4(qty) : 0;
    if (!(q > 0)) { setErr(`Enter the quantity received${unit ? ` (${unit})` : ''}.`); return; }
    save.mutate({
      itemCode: item.itemCode,
      txnType: 'RECEIPT',
      qty: q,
      rate: rate ?? null,
      poNumber: poNumber.trim() || null,
      vendorName: vendorName.trim() || null,
      invoiceNumber: invoiceNumber.trim() || null,
      invoiceDate: invoiceDate || null,
      remarks: remarks.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h2 className="text-lg font-semibold mb-3 text-emerald-700">In — stock received</h2>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700 mb-4">
          <div><ItemLine item={item} /></div>
          <div>Current stock: <b>{fmtQ(onHandOf(item))}{unit && ` ${unit}`}</b></div>
        </div>

        {pos.length > 0 && (
          <div className="mb-3">
            <label className="label">Against PO</label>
            <select className="input" value={pos.some((p) => p.poNumber === poNumber) ? poNumber : ''} onChange={(e) => pickPo(e.target.value)}>
              <option value="">— Not against an open PO —</option>
              {pos.map((p) => (
                <option key={p.poId} value={p.poNumber}>
                  {p.poNumber} · {p.vendorName} · {qtyText(p.qty)} {p.uom}
                  {p.receivedQty ? ` · ${qtyText(p.receivedQty)} in` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          <NumField label={`Quantity received${unit ? ` (${unit})` : ''} *`} text={qtyInput} onText={setQtyInput}
            value={qty} placeholder="=formula OK" digits={4} autoFocus />
          <NumField label={`Rate (₹ per ${unit || 'unit'})`} text={rateText} onText={setRateText}
            value={rate} placeholder="e.g. 450 or =formula" digits={4} />
          <div>
            <label className="label">Value (Qty x Rate)</label>
            <input className="input bg-slate-50" readOnly tabIndex={-1} value={value != null ? `₹ ${money(value)}` : ''} />
          </div>
          <div>
            <label className="label">Vendor</label>
            <input className="input" list="inv-vendors" value={vendorName} onChange={(e) => setVendorName(e.target.value)} maxLength={200}
              placeholder="Who it came from — pick or type" />
            <datalist id="inv-vendors">{(vendors.data ?? []).map((v) => <option key={v.id} value={v.name.trim()} />)}</datalist>
          </div>
          <div>
            <label className="label">Invoice Date</label>
            <input type="date" className="input" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          </div>
          <div>
            <label className="label">Invoice Number</label>
            <input className="input" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2024-001" />
          </div>
          <div>
            <label className="label">PO Number</label>
            <input className="input" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="e.g. VP/2026-27/004" />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Remarks</label>
            <input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        {err && <div className="text-red-700 text-sm mt-3">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary bg-emerald-600 hover:bg-emerald-700" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Add stock'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- History

const TXN_TEXT: Record<string, string> = {
  OPENING: 'Opening',
  RECEIPT: 'In',
  ISSUE: 'Issue',
  ADJUST: 'Correction',
  REVERSAL: 'Reversal',
};

/** Where an issue went (or a legacy row's reference), as the store reads it. */
function refText(r: HistoryRow): string {
  if (!r.refId) return '';
  switch (r.refType) {
    case 'ISSUED_TO': return r.refId;
    case 'JOB_CARD':  return `Job card ${r.refId}`;
    case 'MACHINE':   return `Machine ${r.refId}`;
    case 'DEPT':      return `Dept ${r.refId}`;
    case 'PO':        return `PO ${r.refId}`;
    default:          return r.refType ? `${r.refType} ${r.refId}` : r.refId;
  }
}

function reverseError(e: unknown): string {
  const m = (e as Error).message;
  if (m.startsWith('reversal_would_go_negative:')) {
    const [, have, take] = m.split(':');
    return `Can't reverse this: only ${fmtQ(Number(have))} on hand, and undoing it takes away ${fmtQ(Math.abs(Number(take)))}.`;
  }
  switch (m) {
    case 'reverse_window_passed': return 'This was made more than an hour ago — ask Super Admin to reverse it.';
    case 'already_reversed': return 'This has already been reversed.';
    case 'cannot_reverse_a_reversal': return 'A reversal cannot itself be reversed.';
    case 'inventory_reverse_migration_pending': return 'Reverse needs the latest database update — run the deploy script.';
    case 'item_not_found': return 'This item has been deleted.';
    default: return `Could not reverse: ${m}`;
  }
}

const VERIFY_TEXT: Record<string, { text: string; cls: string }> = {
  PENDING: { text: 'AWAITING CHECK', cls: 'bg-amber-100 text-amber-800' },
  VERIFIED: { text: 'VERIFIED', cls: 'bg-emerald-100 text-emerald-700' },
  REJECTED: { text: 'NOT VERIFIED · STOCK BACK', cls: 'bg-red-100 text-red-700' },
};

/** A Store issue's check, beside its type. */
function VerifyBadge({ r }: { r: HistoryRow }) {
  const v = r.verifyStatus ? VERIFY_TEXT[r.verifyStatus] : null;
  if (!v) return null;
  const who = r.verifiedByName ? `${r.verifiedByName}${r.verifiedOn ? `, ${dateTimeText(r.verifiedOn)}` : ''}` : '';
  return (
    <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${v.cls}`}
      title={[who && `By ${who}`, r.verifyNote].filter(Boolean).join(' — ') || undefined}>
      {v.text}
    </span>
  );
}

function verifyError(e: unknown): string {
  const m = (e as Error).message;
  if (m.startsWith('not_pending')) return 'This issue has already been checked — refresh the list.';
  switch (m) {
    case 'reason_required': return 'Say why it is not verified.';
    case 'store_role_migration_pending': return 'This needs the latest database update — run the store role migration.';
    default: return `Could not save: ${m}`;
  }
}

/**
 * The Store role's issues: those waiting for their check first.  Purchase and
 * Super Admin verify each, or turn it down — which puts its stock back.  The
 * Store sees the same list, to follow what happened to their issues.
 */
function StoreIssuesTab({ categories, onChanged }: { categories: string[]; onChanged: () => void }) {
  const { user } = useAuth();
  const canVerify = can(user, 'VERIFY_STORE_ISSUE');
  const qc = useQueryClient();
  const cats = categories.join(',');
  const [show, setShow] = useState<'pending' | 'all'>('pending');
  const [find, setFind] = useState('');
  // Asked of the server a moment after typing stops, so "All store issues"
  // is searched in full, not only the rows on screen.
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(find.trim()), 300);
    return () => clearTimeout(t);
  }, [find]);
  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['inv-store-issues', show === 'pending' ? 'pending' : 'store', cats, q],
    queryFn: () => api<{ rows: HistoryRow[] }>(`/api/inventory/history?verify=${show === 'pending' ? 'pending' : 'store'}&categories=${encodeURIComponent(cats)}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    // Keep the last list on screen while the next search loads.
    placeholderData: (prev) => prev,
  });
  const rows = data?.rows ?? [];
  const check = useMutation({
    mutationFn: (v: { id: number; approve: boolean; note: string | null }) =>
      api(`/api/inventory/history/${v.id}/verify`, { method: 'POST', body: JSON.stringify({ approve: v.approve, note: v.note }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inv-store-issues'] });
      onChanged();
    },
    onError: (e) => alert(verifyError(e)),
  });

  function reject(r: HistoryRow) {
    const amount = `${fmtQ(Math.abs(Number(r.qtyChange)))}${r.uom ? ` ${r.uom}` : ''}`;
    const note = prompt(`Not verified: ${amount} of ${r.itemName} goes back into stock.\n\nWhy is it not verified?`);
    if (note == null) return;
    if (!note.trim()) { alert('Say why it is not verified.'); return; }
    check.mutate({ id: r.historyId, approve: false, note: note.trim() });
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
          {(['pending', 'all'] as const).map((k) => (
            <button key={k} type="button" onClick={() => setShow(k)}
              className={`rounded px-3 py-1 text-sm font-medium ${show === k ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}>
              {k === 'pending' ? 'Waiting for check' : 'All store issues'}
            </button>
          ))}
        </div>
        <input type="search" value={find} onChange={(e) => setFind(e.target.value)}
          placeholder="Search SKU, item, department, remarks…"
          className="input w-72 max-w-full text-sm" />
        {isFetching && !isLoading && <span className="text-xs text-slate-400">Searching…</span>}
        <p className="text-xs text-slate-500">
          {canVerify
            ? 'Verify each issue the Store made. Not verified puts the quantity back into stock.'
            : 'Your issues are checked by Purchase or Super Admin. One not verified goes back into stock.'}
        </p>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Date</Th>
              <Th>SKU</Th>
              <Th>Item</Th>
              <Th numeric>Qty</Th>
              <Th numeric>Stock in hand</Th>
              <Th>Issued to · Remarks</Th>
              <Th>By</Th>
              <Th>Check</Th>
              {canVerify && <Th>{' '}</Th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="p-6 text-center text-slate-400">Loading…</td></tr>
            ) : isError ? (
              <tr><td colSpan={9} className="p-6 text-center text-red-600">Could not load the store issues.</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-6 text-center text-slate-400">{q ? `No store issues match "${q}".` : show === 'pending' ? 'Nothing waiting for a check.' : 'No store issues yet.'}</td></tr>
            ) : rows.map((r) => {
              const to = refText(r);
              const unit = r.uom?.trim() || '';
              return (
                <tr key={r.historyId} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">{dateTimeText(r.txnDate)}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap font-semibold text-brand">{r.sku || r.itemCode}</td>
                  <td className="px-3 py-1.5">
                    <div className="font-medium text-slate-800">{r.itemName}</div>
                    {r.itemGroup && <div className="text-[11px] text-slate-500">{r.itemGroup}</div>}
                  </td>
                  <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap text-red-700">
                    {fmtQ(Math.abs(Number(r.qtyChange)))}{unit && <span className="font-normal text-slate-500"> {unit}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap"
                    title={`Stock now, this issue already taken out${r.verifyStatus === 'PENDING' ? ' (Not verified puts it back)' : ''}${r.reorderPoint ? ` · reorder at ${fmtQ(Number(r.reorderPoint))}` : ''}`}>
                    {(() => {
                      const left = Number(r.stockNow ?? 0);
                      const low = left <= 0 || (r.reorderPoint != null && Number(r.reorderPoint) > 0 && left <= Number(r.reorderPoint));
                      return (
                        <span className={`font-semibold ${left <= 0 ? 'text-red-700' : low ? 'text-amber-700' : 'text-emerald-700'}`}>
                          {fmtQ(left)}{unit && <span className="font-normal text-slate-500"> {unit}</span>}
                          {left <= 0 ? <span className="ml-1 text-[10px] font-semibold">OUT</span> : low ? <span className="ml-1 text-[10px] font-semibold">LOW</span> : null}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-1.5 max-w-[240px]">
                    {to && <div className="font-medium text-slate-700 truncate" title={to}>{to}</div>}
                    {r.remarks && <div className="text-slate-500 truncate" title={r.remarks}>{r.remarks}</div>}
                  </td>
                  <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{r.byName || '—'}</td>
                  <td className="px-3 py-1.5">
                    <VerifyBadge r={r} />
                    {r.verifyStatus !== 'PENDING' && r.verifiedByName && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{r.verifiedByName}{r.verifiedOn ? `, ${dateTimeText(r.verifiedOn, true)}` : ''}</div>
                    )}
                    {r.verifyNote && <div className="text-[11px] text-slate-600 mt-0.5">{r.verifyNote}</div>}
                  </td>
                  {canVerify && (
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {r.verifyStatus === 'PENDING' && (
                        <>
                          <button className="text-emerald-700 hover:underline font-semibold mr-3 disabled:text-slate-300" disabled={check.isPending}
                            onClick={() => check.mutate({ id: r.historyId, approve: true, note: null })}>Verify</button>
                          <button className="text-red-600 hover:underline font-semibold disabled:text-slate-300" disabled={check.isPending}
                            onClick={() => reject(r)}>Not verified</button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A movement's time as the server wrote it ("2026-10-05 11:54:30"); short leaves out the year. */
function dateTimeText(s: string, short = false): string {
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', ...(short ? {} : { year: 'numeric' }), hour: '2-digit', minute: '2-digit' });
}

/** One movement as a row of the item's History. */
function MovementRow({ r, undone, onReverse, reversing }: {
  r: HistoryRow;
  /** For a reversal: the movement it undoes, when it is in the list. */
  undone?: HistoryRow;
  onReverse?: (r: HistoryRow) => void;
  reversing?: boolean;
}) {
  const qty = Number(r.qtyChange);
  const isIn = qty > 0;
  const isReversal = r.reversesId != null;
  const isReversed = r.reversedById != null;
  const unit = r.uom?.trim() || '';
  // A reversal carries its original's issued-to; the struck-through original shows it.
  const to = isReversal ? '' : refText(r);
  const remarks = isReversal && r.remarks === `Reverses #${r.reversesId}` ? '' : r.remarks;
  return (
    <tr className={`border-t border-slate-100 hover:bg-slate-50 ${isReversed ? 'text-slate-400 line-through opacity-70' : isReversal ? 'bg-amber-50/60' : ''}`}>
      <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">{dateTimeText(r.txnDate)}</td>
      <td className="px-3 py-1.5 whitespace-nowrap">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
          isReversal ? 'bg-amber-100 text-amber-800' : isIn ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
        }`}>{TXN_TEXT[r.txnType] ?? r.txnType}</span>
        {isReversed && r.verifyStatus !== 'REJECTED' && <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-600">REVERSED</span>}
        <VerifyBadge r={r} />
      </td>
      <td className={`px-3 py-1.5 text-right font-semibold whitespace-nowrap ${isIn ? 'text-emerald-700' : 'text-red-700'}`}>
        {isIn ? '+' : ''}{fmtQ(qty)}{unit && <span className="font-normal text-slate-500"> {unit}</span>}
      </td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        {r.value != null && <div className="font-semibold text-amber-700">₹{money(Number(r.value))}</div>}
        {r.rate != null && <div className="text-[10px] text-slate-500">@ ₹{money(Number(r.rate))}{unit ? `/${unit}` : ''}</div>}
        {r.value == null && r.rate == null && <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1.5 whitespace-nowrap text-[11px]">
        {r.poNumber && <div>PO <span className="font-mono">{r.poNumber}</span></div>}
        {(r.invoiceNumber || r.invoiceDate) && (
          <div className="text-slate-500">Inv {r.invoiceNumber || '—'}{r.invoiceDate ? ` · ${poDateText(r.invoiceDate)}` : ''}</div>
        )}
        {!r.poNumber && !r.invoiceNumber && !r.invoiceDate && <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1.5 max-w-[240px]">
        {isReversal && (
          <div className="text-amber-700">
            ↺ Undoes {undone
              ? <>{TXN_TEXT[undone.txnType] ?? undone.txnType} of {dateTimeText(undone.txnDate, true)}</>
              : <>movement #{r.reversesId}</>}
          </div>
        )}
        {to && <div className="font-medium text-slate-700 truncate" title={to}>{r.txnType === 'ISSUE' ? `To: ${to}` : to}</div>}
        {remarks && <div className="text-slate-500 truncate" title={remarks}>{remarks}</div>}
        {!to && !remarks && !isReversal && <span className="text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{r.byName || '—'}</td>
      {onReverse && (
        <td className="px-3 py-1.5 text-right whitespace-nowrap">
          {r.canReverse && (
            <button className="text-red-600 hover:underline text-[11px] font-medium disabled:text-slate-300" disabled={reversing} onClick={() => onReverse(r)}>
              Reverse
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

function MovementHead({ withAction }: { withAction?: boolean }) {
  return (
    <thead className="bg-slate-50 border-b border-slate-200">
      <tr>
        <Th>Date</Th>
        <Th>Type</Th>
        <Th numeric>Qty</Th>
        <Th numeric>Value · Rate</Th>
        <Th>PO / Invoice</Th>
        <Th>Issued to · Remarks</Th>
        <Th>By</Th>
        {withAction && <Th>{' '}</Th>}
      </tr>
    </thead>
  );
}

/** Every movement of one item, newest first, with Reverse for a mistake. */
function HistoryModal({ item, onClose, onChanged }: { item: Item; onClose: () => void; onChanged: () => void }) {
  const { user } = useAuth();
  const superAdmin = user?.rollId === ROLE.SuperAdmin;
  const canManage = can(user, 'MANAGE_INVENTORY');
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inv-item-history', item.itemCode],
    queryFn: () => api<{ rows: HistoryRow[] }>(`/api/inventory/history?code=${encodeURIComponent(item.itemCode)}`),
  });
  const rows = data?.rows ?? [];
  const byId = new Map(rows.map((r) => [r.historyId, r]));
  const reverse = useMutation({
    mutationFn: (id: number) => api(`/api/inventory/history/${id}/reverse`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inv-item-history', item.itemCode] });
      onChanged();
    },
    onError: (e) => alert(reverseError(e)),
  });
  const unit = unitOf(item);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 max-w-6xl w-full mx-2 sm:mx-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-brand">History</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              <ItemLine item={item} /> · On hand: <b className="text-emerald-700">{fmtQ(onHandOf(item))}</b>{unit && ` ${unit}`}
            </div>
          </div>
          <button className="btn-ghost text-sm" onClick={onClose}>Close</button>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="w-full text-xs">
            <MovementHead withAction={canManage} />
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400">Loading…</td></tr>
              ) : isError ? (
                <tr><td colSpan={8} className="p-6 text-center text-red-600">Could not load the history.</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400">No movements yet.</td></tr>
              ) : rows.map((r) => (
                <MovementRow
                  key={r.historyId}
                  r={r}
                  undone={r.reversesId != null ? byId.get(r.reversesId) : undefined}
                  reversing={reverse.isPending}
                  onReverse={!canManage ? undefined : (row) => {
                    const amount = `${fmtQ(Math.abs(Number(row.qtyChange)))}${unit ? ` ${unit}` : ''}`;
                    const what = TXN_TEXT[row.txnType]?.toLowerCase() ?? 'movement';
                    if (confirm(`Reverse this ${what} of ${amount}?\n\nThis ${Number(row.qtyChange) < 0 ? 'adds back' : 'takes away'} ${amount} and keeps both rows in History.`)) {
                      reverse.mutate(row.historyId);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
        {canManage && (
          <div className="text-[11px] text-slate-500 mt-2">
            Reverse writes a mirror row — the original stays in History, struck through.
            {!superAdmin && ' You can reverse a movement within 1 hour of making it; after that, ask Super Admin.'}
          </div>
        )}
      </div>
    </div>
  );
}

const BATCH_TEXT: Record<string, string> = { RECEIPT: 'In', OPENING: 'Opening stock', ADJUST: 'Correction' };

/**
 * The stock on hand batch by batch: what is left of each In, at the rate it
 * came in at, oldest first.  The list's rate is their average (value ÷ qty).
 * An Issue uses up the oldest batch first, so once it is gone the rate is
 * worked out from the batches left — with one left, that batch's own rate.
 */
function BatchesModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const unit = unitOf(item);
  const per = unit ? `/${unit}` : '';
  const rows = batchesOf(item);
  const qty = onHandOf(item);
  const value = valueOf(item);
  const rate = rateOf(item);
  const rates = ratesIn(item);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 max-w-3xl w-full mx-2 sm:mx-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-brand">Stock by rate</h2>
            <div className="text-xs text-slate-500 mt-0.5"><ItemLine item={item} /></div>
          </div>
          <button className="btn-ghost text-sm" onClick={onClose}>Close</button>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Came in</Th>
                <Th numeric>Qty</Th>
                <Th numeric>Rate (₹)</Th>
                <Th numeric>Value (₹)</Th>
                <Th>PO / Invoice</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <div className="font-medium text-slate-700">{b.at ? dateTimeText(b.at) : 'Earlier stock'}</div>
                    <div className="text-[10px] text-slate-500">
                      {b.type ? BATCH_TEXT[b.type] ?? b.type : 'no In recorded'}
                      {i === 0 && rows.length > 1 && (
                        <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">issued first</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-right font-semibold whitespace-nowrap">
                    {fmtQ(b.qty)}{unit && <span className="font-normal text-slate-500"> {unit}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {money(b.rate)}<span className="text-slate-400">{per}</span>
                    {b.priced === false && (
                      <div className="text-[10px] text-slate-400">{b.type === 'RECEIPT' ? 'no rate on this In — ' : ''}at the last rate</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-semibold text-amber-700 whitespace-nowrap">{money(qtyValue(b.qty, b.rate))}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap text-[11px]">
                    {b.poNumber && <div>PO <span className="font-mono">{b.poNumber}</span></div>}
                    {(b.invoiceNumber || b.invoiceDate) && (
                      <div className="text-slate-500">Inv {b.invoiceNumber || '—'}{b.invoiceDate ? ` · ${poDateText(b.invoiceDate)}` : ''}</div>
                    )}
                    {!b.poNumber && !b.invoiceNumber && !b.invoiceDate && <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td className="px-3 py-2 font-semibold text-slate-600">On hand</td>
                <td className="px-3 py-2 text-right font-bold text-emerald-700 whitespace-nowrap">
                  {fmtQ(qty)}{unit && <span className="font-normal text-slate-500"> {unit}</span>}
                </td>
                <td className="px-3 py-2 text-right font-bold whitespace-nowrap">
                  {rate != null ? money(rate) : '—'}<span className="font-normal text-slate-400">{per}</span>
                  {rates > 1 && <div className="text-[10px] font-semibold text-amber-700">average</div>}
                </td>
                <td className="px-3 py-2 text-right font-bold text-amber-700 whitespace-nowrap">{money(value)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="text-[11px] text-slate-500 mt-2 space-y-0.5">
          {rates > 1 && (
            <div>The rate in the list is the average: ₹{money(value)} ÷ {fmtQ(qty)}{unit && ` ${unit}`} = ₹{money(rate ?? 0)}{per}.</div>
          )}
          <div>An Issue uses up the oldest batch first. Once it is gone, the rate is worked out from the batches left — with one left, it is that batch's rate.</div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Reports

/** A report's item-wise row. */
interface ReportItemRow {
  Item_Code: string;
  SKU?: string | null;
  Ref_Code: string | null;
  Item_Name: string;
  Item_Group: string | null;
  Brand: string | null;
  Category: string;
  UOM: string | null;
  Sub_Head: string | null;
  Total_Consumed?: number;
  Total_Received?: number;
  Total_Value: number;
  /** Received report: who it came from in the period. */
  Vendors?: string;
}

/** A report's line: one issue, or one receipt. */
interface ReportLine {
  id: number;
  at: string;
  itemCode: string;
  sku: string;
  refCode: string | null;
  itemName: string;
  itemGroup: string | null;
  brand: string | null;
  category: string;
  uom: string | null;
  qty: number;
  rate: number | null;
  value: number;
  issuedTo: string | null;
  remarks: string | null;
  poNumber: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  type: string;
  byName: string | null;
  /** Received report: the receipt's vendor (its PO's, else the item's usual one). */
  vendor?: string;
}

/** yyyy-mm-dd of a date, in India time (dates show in office time — lib/officeTime). */
const isoDay = (d: Date) => d.toLocaleDateString('en-CA');

/** The reports' quick ranges: this month, last month, this financial year (from April). */
function rangeOf(kind: 'month' | 'last-month' | 'fy'): { start: string; end: string } {
  const today = isoDay(new Date());
  const [y, m] = today.split('-').map(Number) as [number, number];
  const pad = (n: number) => String(n).padStart(2, '0');
  if (kind === 'month') return { start: `${y}-${pad(m)}-01`, end: today };
  if (kind === 'last-month') {
    const ly = m === 1 ? y - 1 : y;
    const lm = m === 1 ? 12 : m - 1;
    const lastDay = new Date(Date.UTC(ly, lm, 0)).getUTCDate();
    return { start: `${ly}-${pad(lm)}-01`, end: `${ly}-${pad(lm)}-${pad(lastDay)}` };
  }
  const fy = m >= 4 ? y : y - 1;
  return { start: `${fy}-04-01`, end: today };
}

type ReportKind = 'consumption' | 'received';
const REPORTS: Record<ReportKind, {
  title: string; blurb: string; path: string; qtyLabel: string; lineLabel: string; empty: string; note: string;
}> = {
  consumption: {
    title: 'Consumption',
    blurb: 'What was issued — item by item, or issue by issue.',
    path: '/api/inventory/reports/consumption',
    qtyLabel: 'Qty Consumed',
    lineLabel: 'Each issue',
    empty: 'Nothing was issued in this period.',
    note: 'Issues go out first in, first out: each is valued at what the oldest stock it used up cost. Avg Rate is the value ÷ the quantity. Reversed issues are left out.',
  },
  received: {
    title: 'Received',
    blurb: 'What came in — item by item, or receipt by receipt with its rate, PO and invoice.',
    path: '/api/inventory/reports/receipts',
    qtyLabel: 'Qty Received',
    lineLabel: 'Each receipt',
    empty: 'Nothing came in during this period.',
    note: 'Each receipt at its own rate; Avg Rate is the value ÷ the quantity. Opening stock counts on the day it was booked. Reversed receipts are left out. Vendor is the one entered on the receipt, else the vendor of the PO it was booked against. Under Each receipt, click a vendor to set or correct it.',
  },
};

type XCell = string | number | null;
interface XlsxReport {
  fileName: string; title: string; subtitle?: string;
  columns: { header: string; kind?: 'text' | 'qty' | 'money' }[];
  rows: XCell[][]; totals?: XCell[];
}

/** "1 Oct 2026 to 31 Oct 2026", or "All dates". */
function rangeText(start: string, end: string): string {
  if (!start && !end) return 'All dates';
  return `${start ? poDateText(start) : 'the start'} to ${end ? poDateText(end) : 'today'}`;
}

/** A report as the page shows it, downloaded as an Excel file. */
async function downloadXlsx(report: XlsxReport): Promise<void> {
  const res = await fetch('/api/inventory/reports/export.xlsx', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${getToken() ?? ''}` },
    body: JSON.stringify(report),
  });
  if (!res.ok) throw new Error(`Could not make the Excel file (${res.status}).`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'report.xlsx';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The Download Excel button: one file of what is on screen. */
function ExcelButton({ build, disabled }: { build: () => XlsxReport; disabled?: boolean }) {
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" className="btn-ghost text-sm text-emerald-700" disabled={disabled || busy}
      title="Download what is shown, as an Excel file"
      onClick={async () => {
        setBusy(true);
        try { await downloadXlsx(build()); } catch (e) { alert((e as Error).message); } finally { setBusy(false); }
      }}>
      {busy ? 'Preparing…' : '⬇ Download Excel'}
    </button>
  );
}

/**
 * Reports: Consumption (issued) and Received (came in) between two dates —
 * item-wise totals, or every issue / receipt — narrowed to an item by typing.
 * Issues are valued first in, first out, as the stock is.
 */
interface FormulaRow {
  id: number; at: string; itemCode: string; itemName: string; uom: string;
  issuedTo: string; toJob: boolean; client: string; product: string;
  how: string; expected: number; issued: number; extra: number; extraPct: number | null; byName: string;
}

/**
 * Lamination check: each issue of film made by the formula, with what the
 * formula expected (L x W x g/m2 / 3100 / 500 x sheets) and what was issued.
 * Shows only those over by default.
 */
function FormulaCheckReport({ start, end, setStart, setEnd, setRange }: {
  start: string; end: string; setStart: (v: string) => void; setEnd: (v: string) => void;
  setRange: (k: 'month' | 'last-month' | 'fy') => void;
}) {
  const [onlyOver, setOnlyOver] = useState(true);
  const [find, setFind] = useState('');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inv-formula-check', start, end],
    queryFn: () => api<{ ready: boolean; rows: FormulaRow[] }>(`/api/inventory/reports/formula-check?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
    placeholderData: (prev) => prev,
  });
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const all = (data?.rows ?? []).filter((r) => words.every((w) => `${r.itemName} ${r.issuedTo} ${r.client} ${r.product} ${r.byName}`.toLowerCase().includes(w)));
  // Over: more than a hundredth of a kg above the formula.
  const over = all.filter((r) => r.extra > 0.01);
  const rows = onlyOver ? over : all;
  const kg = (v: number) => v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const sum = (k: 'expected' | 'issued' | 'extra') => rows.reduce((p, r) => p + r[k], 0);
  const quick = 'text-xs font-medium text-brand hover:underline';
  return (
    <div>
      <p className="mb-3 text-xs text-slate-500">
        Lamination film issued by the formula — L × W (inch) × g/m² ÷ 3100 ÷ 500 × sheets (Gloss 10, Matt 11, Met PET 16) —
        against what was actually issued. Reversed issues, and Store issues turned down, are left out.
      </p>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div><label className="label">From</label><input type="date" className="input !w-auto" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div><label className="label">To</label><input type="date" className="input !w-auto" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        <button type="button" className={quick} onClick={() => setRange('month')}>This month</button>
        <button type="button" className={quick} onClick={() => setRange('last-month')}>Last month</button>
        <label className="flex items-center gap-1.5 text-sm text-slate-700">
          <input type="checkbox" checked={onlyOver} onChange={(e) => setOnlyOver(e.target.checked)} />
          Only more than expected ({over.length} of {all.length})
        </label>
        <input className="input !w-64 ml-auto" placeholder="Item, job, client, who…" value={find} onChange={(e) => setFind(e.target.value)} />
        <ExcelButton disabled={!data?.ready || rows.length === 0} build={() => ({
          fileName: `Lamination check ${start || 'all'} to ${end || 'today'}`,
          title: 'Lamination check - film issued vs the formula',
          subtitle: [rangeText(start, end), onlyOver ? 'only more than expected' : 'all issues', find.trim() && `search: ${find.trim()}`].filter(Boolean).join(' · '),
          columns: [{ header: 'Date' }, { header: 'Film' }, { header: 'Issued to' }, { header: 'Client' }, { header: 'Product' },
            { header: 'Worked out from' }, { header: 'Expected', kind: 'qty' }, { header: 'Issued', kind: 'qty' },
            { header: 'More than expected', kind: 'qty' }, { header: 'More %' }, { header: 'UOM' }, { header: 'By' }],
          rows: rows.map((r) => [
            new Date(r.at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }),
            r.itemName, r.issuedTo, r.client || null, r.product || null, r.how, r.expected, r.issued, r.extra,
            r.extraPct != null ? `${r.extraPct.toFixed(1)}%` : null, r.uom, r.byName]),
          totals: [`Total (${rows.length} issues)`, null, null, null, null, null, sum('expected'), sum('issued'), sum('extra'), null, null, null],
        })} />
      </div>
      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {isError && <div className="text-sm text-red-600">Could not load the report.</div>}
      {data && !data.ready && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This needs the latest database update (migration_2026_10_24_issue_formula.sql).
        </div>
      )}
      {data?.ready && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Film</th>
                <th className="px-3 py-2 text-left">Issued to</th>
                <th className="px-3 py-2 text-left">Worked out from</th>
                <th className="px-3 py-2 text-right">Expected</th>
                <th className="px-3 py-2 text-right">Issued</th>
                <th className="px-3 py-2 text-right">More than expected</th>
                <th className="px-3 py-2 text-left">By</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400">{onlyOver ? 'Nothing issued over the formula in this period.' : 'No film issued by the formula in this period.'}</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-1.5 whitespace-nowrap text-xs text-slate-600">{new Date(r.at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })}</td>
                  <td className="px-3 py-1.5">{r.itemName}</td>
                  <td className="px-3 py-1.5">
                    <span className={r.toJob ? 'font-mono text-xs font-semibold text-brand-700' : ''}>{r.issuedTo}</span>
                    {(r.client || r.product) && <div className="text-xs text-slate-500">{[r.client, r.product].filter(Boolean).join(' · ')}</div>}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-600">{r.how}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{kg(r.expected)} {r.uom}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{kg(r.issued)} {r.uom}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${r.extra > 0.01 ? 'text-red-700' : 'text-emerald-700'}`}>
                    {r.extra > 0.01 ? '+' : ''}{kg(r.extra)} {r.uom}
                    {r.extraPct != null && <div className="text-[11px] font-normal">{r.extraPct > 0 ? '+' : ''}{r.extraPct.toFixed(1)}%</div>}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-600">{r.byName}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                <tr>
                  <td className="px-3 py-2" colSpan={4}>Total ({rows.length} issues)</td>
                  <td className="px-3 py-2 text-right tabular-nums">{kg(sum('expected'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{kg(sum('issued'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">{sum('extra') > 0 ? '+' : ''}{kg(sum('extra'))}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}


interface StockSumRow {
  Item_Code: string; SKU: string; Ref_Code: string | null; Item_Name: string; Item_Group: string | null;
  Brand: string | null; Category: string; UOM: string | null; Sub_Head: string | null;
  openQty: number; openValue: number; inQty: number; inValue: number; outQty: number; outValue: number;
  adjQty: number; adjValue: number; closeQty: number; closeValue: number;
}

/**
 * Stock Summary: opening stock on the From date, what came in, what was
 * issued, corrections, and closing stock on the To date - by Item Group, or
 * item by item.  Valued first in, first out, as the Stock tab is.
 */
function StockSummaryReport({ categories, start, end, setStart, setEnd, setRange }: {
  categories: string[]; start: string; end: string; setStart: (v: string) => void; setEnd: (v: string) => void;
  setRange: (k: 'month' | 'last-month' | 'fy') => void;
}) {
  const [view, setView] = useState<'groups' | 'items'>('groups');
  const [find, setFind] = useState('');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inv-stock-summary', start, end],
    queryFn: () => api<{ rows: StockSumRow[] }>(`/api/inventory/reports/stock-summary?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
    placeholderData: (prev) => prev,
  });
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const items = (data?.rows ?? [])
    .filter((r) => categories.includes(r.Category)
      && words.every((w) => `${r.Item_Group ?? ''} ${r.Item_Name} ${r.Brand ?? ''} ${r.SKU} ${r.Ref_Code ?? ''}`.toLowerCase().includes(w)))
    .sort((a, b) => (a.Item_Group ?? '').localeCompare(b.Item_Group ?? '') || skuOrder(a.SKU, b.SKU));
  const NO_GROUP = '(no group)';
  type Sum = { name: string; items: number; uoms: Set<string>; openQty: number; openValue: number; inQty: number; inValue: number;
    outQty: number; outValue: number; adjValue: number; closeQty: number; closeValue: number };
  const blank = (name: string): Sum => ({ name, items: 0, uoms: new Set(), openQty: 0, openValue: 0, inQty: 0, inValue: 0,
    outQty: 0, outValue: 0, adjValue: 0, closeQty: 0, closeValue: 0 });
  const add = (t: Sum, r: StockSumRow) => {
    t.items += 1; if (r.UOM) t.uoms.add(r.UOM.toUpperCase());
    t.openQty += r.openQty; t.openValue += r.openValue; t.inQty += r.inQty; t.inValue += r.inValue;
    t.outQty += r.outQty; t.outValue += r.outValue; t.adjValue += r.adjValue; t.closeQty += r.closeQty; t.closeValue += r.closeValue;
  };
  const groups = (() => {
    const by = new Map<string, Sum>();
    for (const r of items) {
      const k = r.Item_Group?.trim() || NO_GROUP;
      const g = by.get(k) ?? blank(k);
      add(g, r); by.set(k, g);
    }
    return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();
  const total = blank('Total');
  for (const r of items) add(total, r);
  // A quantity only adds up in one unit.
  const unitOf = (t: Sum) => (t.uoms.size === 1 ? [...t.uoms][0]! : '');
  const q = (v: number, u: string) => (u ? <>{fmtQ(v)} <span className="text-slate-500 font-normal">{u}</span></> : '—');
  const cents = (v: number) => Math.round(v * 100) / 100;
  const quick = 'text-xs font-medium text-brand hover:underline';
  const pill = (on: boolean) => `px-3 py-1.5 text-sm font-semibold rounded ${on ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`;
  const th = 'px-3 py-2 text-right whitespace-nowrap';
  const td = 'px-3 py-2 text-right whitespace-nowrap tabular-nums';

  const excel = (): XlsxReport => {
    const base = {
      fileName: `Stock Summary ${view === 'groups' ? 'Group-wise' : 'Item-wise'} ${start || 'all'} to ${end || 'today'}`,
      title: `Stock Summary - ${view === 'groups' ? 'Group-wise' : 'Item-wise'}`,
      subtitle: [rangeText(start, end), 'opening at the start of the first day, closing at the end of the last', find.trim() && `search: ${find.trim()}`].filter(Boolean).join(' · '),
    };
    const money = { kind: 'money' as const }; const qty = { kind: 'qty' as const };
    const valueCols = [{ header: 'Opening Qty', ...qty }, { header: 'Opening Value (Rs)', ...money }, { header: 'In Qty', ...qty },
      { header: 'In Value (Rs)', ...money }, { header: 'Out Qty', ...qty }, { header: 'Out Value (Rs)', ...money },
      { header: 'Corrections (Rs)', ...money }, { header: 'Closing Qty', ...qty }, { header: 'Closing Value (Rs)', ...money }];
    const sumCells = (t: Sum, u: string): XCell[] => [u ? t.openQty : null, cents(t.openValue), u ? t.inQty : null, cents(t.inValue),
      u ? t.outQty : null, cents(t.outValue), cents(t.adjValue), u ? t.closeQty : null, cents(t.closeValue)];
    if (view === 'groups') {
      return { ...base,
        columns: [{ header: 'Group' }, { header: 'Items', ...qty }, { header: 'UOM' }, ...valueCols],
        rows: groups.map((g) => [g.name, g.items, unitOf(g) || null, ...sumCells(g, unitOf(g))]),
        totals: ['Total', total.items, unitOf(total) || null, ...sumCells(total, unitOf(total))] };
    }
    return { ...base,
      columns: [{ header: 'Group' }, { header: 'SKU' }, { header: 'Item Name' }, { header: 'Brand' }, { header: 'UOM' }, ...valueCols],
      rows: items.map((r) => [r.Item_Group || null, r.SKU, r.Item_Name, r.Brand || null, r.UOM || null,
        r.openQty, r.openValue, r.inQty, r.inValue, r.outQty, r.outValue, r.adjValue, r.closeQty, r.closeValue]),
      totals: ['Total', null, null, null, unitOf(total) || null, ...sumCells(total, unitOf(total))] };
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs text-slate-500">
          Opening stock at the start of the From date, what came in and went out, and closing stock at the end of the To date.
        </p>
        <div className="inline-flex rounded-md bg-slate-100 p-1">
          <button type="button" className={pill(view === 'groups')} onClick={() => setView('groups')}>Group-wise</button>
          <button type="button" className={pill(view === 'items')} onClick={() => setView('items')}>Item-wise</button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
          <input type="date" className="input text-sm" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
          <input type="date" className="input text-sm" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="flex items-center gap-3 pb-2">
          <button type="button" className={quick} onClick={() => setRange('month')}>This month</button>
          <button type="button" className={quick} onClick={() => setRange('last-month')}>Last month</button>
          <button type="button" className={quick} onClick={() => setRange('fy')}>This year (from April)</button>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">Item</label>
          <input className="input text-sm" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Item, group, SKU or item code" />
        </div>
        <div className="text-sm text-slate-600 pb-2">
          Opening <b className="text-slate-800">₹{money(cents(total.openValue))}</b> · Closing <b className="text-amber-700">₹{money(cents(total.closeValue))}</b>
        </div>
        <div className="pb-1"><ExcelButton disabled={isLoading || items.length === 0} build={excel} /></div>
      </div>
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">{view === 'groups' ? 'Group' : 'Item'}</th>
              {view === 'groups' && <th className={th}>Items</th>}
              <th className={th}>Opening</th>
              <th className={th}>Opening ₹</th>
              <th className={th}>In</th>
              <th className={th}>In ₹</th>
              <th className={th}>Out</th>
              <th className={th}>Out ₹</th>
              <th className={th} title="Stock corrections up or down">Corrections ₹</th>
              <th className={th}>Closing</th>
              <th className={th}>Closing ₹</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={11} className="p-4 text-center text-slate-500">Loading…</td></tr>
            ) : isError ? (
              <tr><td colSpan={11} className="p-4 text-center text-red-600">Could not load the report.</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={11} className="p-6 text-center text-slate-400">No stock or movement in this period.</td></tr>
            ) : view === 'groups' ? groups.map((g) => {
              const u = unitOf(g);
              return (
                <tr key={g.name} className="border-t border-slate-100 hover:bg-yellow-100">
                  <td className="px-3 py-2 font-semibold">{g.name}</td>
                  <td className={td}>{g.items}</td>
                  <td className={td}>{q(g.openQty, u)}</td>
                  <td className={td}>{money(cents(g.openValue))}</td>
                  <td className={`${td} text-emerald-700`}>{q(g.inQty, u)}</td>
                  <td className={`${td} text-emerald-700`}>{money(cents(g.inValue))}</td>
                  <td className={`${td} text-red-700`}>{q(g.outQty, u)}</td>
                  <td className={`${td} text-red-700`}>{money(cents(g.outValue))}</td>
                  <td className={td}>{Math.abs(g.adjValue) > 0.004 ? money(cents(g.adjValue)) : '—'}</td>
                  <td className={`${td} font-semibold`}>{q(g.closeQty, u)}</td>
                  <td className={`${td} font-bold text-amber-700`}>{money(cents(g.closeValue))}</td>
                </tr>
              );
            }) : items.map((r) => (
              <tr key={r.Item_Code} className="border-t border-slate-100 hover:bg-yellow-100">
                <td className="px-3 py-2">
                  <div className="font-semibold">{r.Item_Name}</div>
                  <div className="text-[11px] text-slate-500"><span className="font-mono font-bold text-brand">{r.SKU}</span>{r.Item_Group ? ` · ${r.Item_Group}` : ''}</div>
                </td>
                <td className={td}>{q(r.openQty, r.UOM ?? ' ')}</td>
                <td className={td}>{money(r.openValue)}</td>
                <td className={`${td} text-emerald-700`}>{q(r.inQty, r.UOM ?? ' ')}</td>
                <td className={`${td} text-emerald-700`}>{money(r.inValue)}</td>
                <td className={`${td} text-red-700`}>{q(r.outQty, r.UOM ?? ' ')}</td>
                <td className={`${td} text-red-700`}>{money(r.outValue)}</td>
                <td className={td}>{Math.abs(r.adjValue) > 0.004 ? money(r.adjValue) : '—'}</td>
                <td className={`${td} font-semibold`}>{q(r.closeQty, r.UOM ?? ' ')}</td>
                <td className={`${td} font-bold text-amber-700`}>{money(r.closeValue)}</td>
              </tr>
            ))}
          </tbody>
          {items.length > 0 && (
            <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <tr>
                <td className="px-3 py-2">Total</td>
                {view === 'groups' && <td className={td}>{total.items}</td>}
                <td className={td}>{q(total.openQty, unitOf(total))}</td>
                <td className={td}>₹{money(cents(total.openValue))}</td>
                <td className={td}>{q(total.inQty, unitOf(total))}</td>
                <td className={td}>₹{money(cents(total.inValue))}</td>
                <td className={td}>{q(total.outQty, unitOf(total))}</td>
                <td className={td}>₹{money(cents(total.outValue))}</td>
                <td className={td}>{Math.abs(total.adjValue) > 0.004 ? `₹${money(cents(total.adjValue))}` : '—'}</td>
                <td className={td}>{q(total.closeQty, unitOf(total))}</td>
                <td className={`${td} text-amber-700`}>₹{money(cents(total.closeValue))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        Valued first in, first out, as the Stock tab: opening and closing are what the batches on hand cost then; Out is what the issues cost.
        In is receipts and opening stock booked. A quantity is shown only where every item shares one unit. Reversed movements are left out.
      </p>
    </div>
  );
}

function ReportsTab({ categories }: { categories: string[] }) {
  const [kind, setKind] = useState<ReportKind | 'formula' | 'stock'>('consumption');
  // 'subhead' aggregates the loaded `rows` by Sub_Head client-side - no new
  // round trip, since item-wise totals already carry each item's Sub_Head.
  const [view, setView] = useState<'items' | 'subhead' | 'lines'>('items');
  const [start, setStart] = useState(() => rangeOf('month').start);
  const [end, setEnd] = useState(() => rangeOf('month').end);
  const [find, setFind] = useState('');
  // Received only: one vendor's receipts (the totals are then of those alone).
  const [vendor, setVendor] = useState('');
  // Received, each receipt: Purchase can set the vendor of a receipt booked without one.
  const { user } = useAuth();
  const qc = useQueryClient();
  const canSetVendor = can(user, 'MANAGE_INVENTORY');
  const [vendorOf, setVendorOf] = useState<ReportLine | null>(null);
  const cfg = REPORTS[kind === 'formula' || kind === 'stock' ? 'consumption' : kind];
  const byVendor = kind === 'received';
  const { data, isLoading, isError } = useQuery({
    enabled: kind !== 'formula' && kind !== 'stock',
    queryKey: ['inv-report', kind, start, end, byVendor ? vendor : ''],
    queryFn: () => api<{ rows: ReportItemRow[]; lines: ReportLine[]; vendors?: string[] }>(
      `${cfg.path}?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${byVendor && vendor ? `&vendor=${encodeURIComponent(vendor)}` : ''}`,
    ),
    placeholderData: (prev) => prev,
  });
  // The chosen vendor stays in the list even when the dates no longer hold anything of theirs.
  const vendorList = [...new Set([...(data?.vendors ?? []), ...(vendor ? [vendor] : [])])];

  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (hay: string) => words.every((w) => hay.toLowerCase().includes(w));
  // Item-wise in SKU order, as the stock list is.
  const rows = (data?.rows ?? []).filter((r) => categories.includes(r.Category)
    && matches(`${r.Item_Group ?? ''} ${r.Item_Name} ${r.Brand ?? ''} ${r.SKU ?? r.Item_Code} ${r.Ref_Code ?? ''} ${r.Vendors ?? ''}`))
    .sort((a, b) => skuOrder(a.SKU, b.SKU));
  const lines = (data?.lines ?? []).filter((l) => categories.includes(l.category)
    && matches(`${l.itemGroup ?? ''} ${l.itemName} ${l.brand ?? ''} ${l.sku} ${l.refCode ?? ''} ${l.issuedTo ?? ''} ${l.poNumber ?? ''} ${l.invoiceNumber ?? ''} ${l.vendor ?? ''}`));
  const qtyOf = (r: ReportItemRow) => Number(kind === 'consumption' ? r.Total_Consumed : r.Total_Received) || 0;
  const sumPaise = (vs: number[]) => vs.reduce((p, v) => p + Math.round((Number(v) || 0) * 100), 0) / 100;

  // Sub Head-wise view: aggregate the filtered item rows by their Sub_Head.
  // Qty total only has a meaning when every item under the sub head shares a
  // UOM — e.g. all KG - so a mixed one shows as — in the qty column.
  const NO_SUB = '(no sub head)';
  const subHeadRows = (() => {
    if (view !== 'subhead') return [];
    const by = new Map<string, { subHead: string; items: number; qty: number; value: number; uoms: Set<string> }>();
    for (const r of rows) {
      const k = r.Sub_Head?.trim() || NO_SUB;
      const cell = by.get(k) ?? { subHead: k, items: 0, qty: 0, value: 0, uoms: new Set<string>() };
      cell.items += 1;
      cell.qty += qtyOf(r);
      cell.value += Number(r.Total_Value) || 0;
      if (r.UOM) cell.uoms.add(r.UOM.toUpperCase());
      by.set(k, cell);
    }
    return [...by.values()]
      .map((c) => ({ ...c, uom: c.uoms.size === 1 ? [...c.uoms][0]! : '' }))
      .sort((a, b) => b.value - a.value);
  })();

  const total = view === 'items' ? sumPaise(rows.map((r) => r.Total_Value))
    : view === 'subhead' ? sumPaise(subHeadRows.map((r) => r.value))
    : sumPaise(lines.map((l) => l.value));
  // A quantity total only makes sense in one unit.
  const units = new Set((view === 'items' ? rows.map((r) => r.UOM) : lines.map((l) => l.uom)).map((u) => (u ?? '').toUpperCase()));
  const totalQty = units.size === 1
    ? (view === 'items' ? rows.reduce((p, r) => p + qtyOf(r), 0)
      : view === 'subhead' ? subHeadRows.reduce((p, r) => p + r.qty, 0)
      : lines.reduce((p, l) => p + (Number(l.qty) || 0), 0))
    : null;
  const unit = units.size === 1 ? [...units][0] : '';
  const setRange = (k: 'month' | 'last-month' | 'fy') => { const r = rangeOf(k); setStart(r.start); setEnd(r.end); };
  const quick = 'text-xs font-medium text-brand hover:underline';
  const pill = (on: boolean) => `px-3 py-1.5 text-sm font-semibold rounded ${on ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-900'}`;
  const count = view === 'items' ? rows.length
    : view === 'subhead' ? subHeadRows.length
    : lines.length;
  const countLabel = view === 'items' ? 'item(s)'
    : view === 'subhead' ? 'sub head(s)'
    : 'line(s)';

  /** The table on screen - this report, this view, these filters - for Excel. */
  const excel = (): XlsxReport => {
    const viewName = view === 'items' ? 'Item-wise' : view === 'subhead' ? 'Sub Head-wise' : cfg.lineLabel;
    const base = {
      fileName: `${cfg.title} ${viewName} ${start || 'all'} to ${end || 'today'}`,
      title: `${cfg.title} - ${viewName}`,
      subtitle: [rangeText(start, end), byVendor && vendor && `vendor: ${vendor}`, find.trim() && `search: ${find.trim()}`].filter(Boolean).join(' · '),
    };
    const qtyUnit = totalQty != null ? unit || null : null;
    if (view === 'subhead') {
      return { ...base,
        columns: [{ header: 'Sub Head' }, { header: 'Items', kind: 'qty' }, { header: cfg.qtyLabel, kind: 'qty' }, { header: 'UOM' }, { header: 'Value (Rs)', kind: 'money' }],
        rows: subHeadRows.map((r) => [r.subHead, r.items, r.uom ? r.qty : null, r.uom || null, r.value]),
        totals: ['Total', subHeadRows.reduce((p, r) => p + r.items, 0), totalQty, qtyUnit, total] };
    }
    if (view === 'items') {
      return { ...base,
        columns: [{ header: 'SKU' }, { header: 'Group' }, { header: 'Item Name' }, { header: 'Brand' }, { header: 'Item Code' },
          ...(byVendor ? [{ header: 'Vendor' }] : []), { header: cfg.qtyLabel, kind: 'qty' as const }, { header: 'UOM' },
          { header: 'Avg Rate (Rs)', kind: 'money' as const }, { header: 'Value (Rs)', kind: 'money' as const }],
        rows: rows.map((r): XCell[] => {
          const qty = qtyOf(r); const value = Number(r.Total_Value) || 0;
          return [r.SKU || r.Item_Code, r.Item_Group || null, r.Item_Name, r.Brand || null, r.Ref_Code || null,
            ...(byVendor ? [r.Vendors || null] : []), qty, r.UOM || null, qty > 0 && value > 0 ? value / qty : null, value];
        }),
        totals: ['Total', null, null, null, null, ...(byVendor ? [null] : []), totalQty, qtyUnit, null, total] };
    }
    const received = kind === 'received';
    return { ...base,
      columns: [{ header: 'Date' }, { header: 'SKU' }, { header: 'Item' }, { header: 'Group' }, { header: 'Item Code' },
        { header: 'Qty', kind: 'qty' }, { header: 'UOM' }, { header: 'Rate (Rs)', kind: 'money' }, { header: 'Value (Rs)', kind: 'money' },
        ...(received
          ? [{ header: 'Vendor' }, { header: 'PO' }, { header: 'Invoice' }, { header: 'Invoice Date' }]
          : [{ header: 'Issued to' }, { header: 'Remarks' }]),
        { header: 'By' }],
      rows: lines.map((l): XCell[] => [dateTimeText(l.at), l.sku, l.itemName + (l.type === 'OPENING' ? ' (opening stock)' : ''), l.itemGroup || null,
        l.refCode || null, Number(l.qty) || 0, l.uom || null, l.rate, Number(l.value) || 0,
        ...(received
          ? [l.vendor || null, l.poNumber || null, l.invoiceNumber || null, l.invoiceDate ? poDateText(l.invoiceDate) : null]
          : [l.issuedTo || null, l.remarks || null]),
        l.byName || null]),
      totals: ['Total', null, null, null, null, totalQty, qtyUnit, null, total,
        ...(received ? [null, null, null, null] : [null, null]), null] };
  };

  const reportPills = (
    <div className="inline-flex rounded-md bg-slate-100 p-1">
      {(Object.keys(REPORTS) as ReportKind[]).map((k) => (
        <button key={k} type="button" className={pill(kind === k)} onClick={() => setKind(k)}>{REPORTS[k].title}</button>
      ))}
      <button type="button" className={pill(kind === 'formula')} onClick={() => setKind('formula')}>Lamination check</button>
      <button type="button" className={pill(kind === 'stock')} onClick={() => setKind('stock')}>Stock Summary</button>
    </div>
  );
  if (kind === 'stock') {
    return (
      <div>
        <div className="mb-3">{reportPills}</div>
        <StockSummaryReport categories={categories} start={start} end={end} setStart={setStart} setEnd={setEnd} setRange={setRange} />
      </div>
    );
  }
  if (kind === 'formula') {
    return (
      <div>
        <div className="mb-3">{reportPills}</div>
        <FormulaCheckReport start={start} end={end} setStart={setStart} setEnd={setEnd} setRange={setRange} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        {reportPills}
        <div className="inline-flex rounded-md bg-slate-100 p-1">
          <button type="button" className={pill(view === 'items')} onClick={() => setView('items')}>Item-wise</button>
          <button type="button" className={pill(view === 'subhead')} onClick={() => setView('subhead')}>Sub Head-wise</button>
          <button type="button" className={pill(view === 'lines')} onClick={() => setView('lines')}>{cfg.lineLabel}</button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">{cfg.blurb}</p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
          <input type="date" className="input text-sm" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
          <input type="date" className="input text-sm" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="flex items-center gap-3 pb-2">
          <button type="button" className={quick} onClick={() => setRange('month')}>This month</button>
          <button type="button" className={quick} onClick={() => setRange('last-month')}>Last month</button>
          <button type="button" className={quick} onClick={() => setRange('fy')}>This year (from April)</button>
          <button type="button" className={quick} onClick={() => { setStart(''); setEnd(''); }}>All dates</button>
        </div>
        {byVendor && (
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Vendor</label>
            <select className="input text-sm min-w-[200px] max-w-[280px]" value={vendor} onChange={(e) => setVendor(e.target.value)}>
              <option value="">All vendors</option>
              {vendorList.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-500 mb-1">Item</label>
          <input className="input text-sm" value={find} onChange={(e) => setFind(e.target.value)} placeholder={byVendor ? 'Item, group, SKU, item code or vendor' : 'Item, group, SKU or item code'} />
        </div>
        <div className="text-sm text-slate-600 pb-2">
          {count} {countLabel} · <b className="text-amber-700">₹{money(total)}</b>
        </div>
        <div className="pb-1"><ExcelButton disabled={isLoading || count === 0} build={excel} /></div>
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        {view === 'subhead' ? (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Sub Head</Th>
                <Th numeric>Items</Th>
                <Th numeric>{cfg.qtyLabel}</Th>
                <Th numeric>Value (₹)</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={4} className="p-4 text-center text-slate-500">Loading…</td></tr>
              ) : isError ? (
                <tr><td colSpan={4} className="p-4 text-center text-red-600">Could not load the report.</td></tr>
              ) : subHeadRows.length === 0 ? (
                <tr><td colSpan={4} className="p-6 text-center text-slate-400">{cfg.empty}</td></tr>
              ) : subHeadRows.map((r) => (
                <tr key={r.subHead} className="border-t border-slate-100 hover:bg-yellow-100">
                  <td className="px-3 py-2 font-semibold">{r.subHead}</td>
                  <td className="px-3 py-2 text-right">{r.items}</td>
                  <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${kind === 'consumption' ? 'text-red-700' : 'text-emerald-700'}`}>
                    {r.uom ? <>{fmtQ(r.qty)} <span className="font-normal text-slate-500">{r.uom}</span></> : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-amber-700">{money(r.value)}</td>
                </tr>
              ))}
            </tbody>
            {subHeadRows.length > 0 && (
              <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                <tr>
                  <td className="px-3 py-2 font-semibold text-slate-600">Total</td>
                  <td className="px-3 py-2 text-right font-semibold">{subHeadRows.reduce((p, r) => p + r.items, 0)}</td>
                  <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{totalQty != null ? <>{fmtQ(totalQty)} {unit}</> : '—'}</td>
                  <td className="px-3 py-2 text-right font-bold text-amber-700">₹{money(total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : view === 'items' ? (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>SKU</Th>
                <Th>Group</Th>
                <Th>Item Name</Th>
                <Th>Brand</Th>
                <Th>Item Code</Th>
                {byVendor && <Th>Vendor</Th>}
                <Th numeric>{cfg.qtyLabel}</Th>
                <Th numeric>Avg Rate (₹)</Th>
                <Th numeric>Value (₹)</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="p-4 text-center text-slate-500">Loading…</td></tr>
              ) : isError ? (
                <tr><td colSpan={9} className="p-4 text-center text-red-600">Could not load the report.</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-slate-400">{byVendor && vendor ? `Nothing came in from ${vendor} in this period.` : cfg.empty}</td></tr>
              ) : rows.map((r) => {
                const qty = qtyOf(r);
                const value = Number(r.Total_Value) || 0;
                return (
                  <tr key={r.Item_Code} className="border-t border-slate-100 hover:bg-yellow-100">
                    <td className="px-3 py-2 font-mono text-xs font-bold text-brand whitespace-nowrap">{r.SKU || r.Item_Code}</td>
                    <td className="px-3 py-2 text-slate-600">{r.Item_Group || '—'}</td>
                    <td className="px-3 py-2 font-semibold">{r.Item_Name}</td>
                    <td className="px-3 py-2">{r.Brand || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.Ref_Code || '—'}</td>
                    {byVendor && <td className="px-3 py-2 text-slate-700">{r.Vendors || '—'}</td>}
                    <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${kind === 'consumption' ? 'text-red-700' : 'text-emerald-700'}`}>
                      {fmtQ(qty)}{r.UOM && <span className="font-normal text-slate-500"> {r.UOM}</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{qty > 0 && value > 0 ? money(value / qty) : '—'}</td>
                    <td className="px-3 py-2 text-right font-bold text-amber-700">{money(value)}</td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                <tr>
                  <td colSpan={byVendor ? 6 : 5} className="px-3 py-2 text-right font-semibold text-slate-600">Total</td>
                  <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{totalQty != null ? <>{fmtQ(totalQty)} {unit}</> : ''}</td>
                  <td />
                  <td className="px-3 py-2 text-right font-bold text-amber-700">₹{money(total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Date</Th>
                <Th>Item</Th>
                <Th numeric>Qty</Th>
                <Th numeric>Rate (₹)</Th>
                <Th numeric>Value (₹)</Th>
                {kind === 'consumption' ? <Th>Issued to · Remarks</Th> : <><Th>Vendor</Th><Th>PO</Th><Th>Invoice</Th></>}
                <Th>By</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="p-4 text-center text-slate-500">Loading…</td></tr>
              ) : isError ? (
                <tr><td colSpan={9} className="p-4 text-center text-red-600">Could not load the report.</td></tr>
              ) : lines.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-slate-400">{cfg.empty}</td></tr>
              ) : lines.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-yellow-100">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{dateTimeText(l.at)}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{l.itemName}</div>
                    <div className="text-[11px] text-slate-500">
                      <span className="font-mono font-bold text-brand">{l.sku}</span>{l.itemGroup ? ` · ${l.itemGroup}` : ''}
                      {l.refCode && <> · <span className="font-mono">{l.refCode}</span></>}
                      {l.type === 'OPENING' && <> · Opening stock</>}
                    </div>
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${kind === 'consumption' ? 'text-red-700' : 'text-emerald-700'}`}>
                    {fmtQ(l.qty)}{l.uom && <span className="font-normal text-slate-500"> {l.uom}</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{l.rate != null ? money(l.rate) : '—'}</td>
                  <td className="px-3 py-2 text-right font-bold text-amber-700">{money(Number(l.value) || 0)}</td>
                  {kind === 'consumption' ? (
                    <td className="px-3 py-2 text-slate-600">{[l.issuedTo, l.remarks].filter(Boolean).join(' · ') || '—'}</td>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-slate-700">
                        {canSetVendor ? (
                          <button type="button" className={`text-left hover:underline ${l.vendor ? '' : 'font-medium text-brand'}`}
                            title="Set who this receipt came from" onClick={() => setVendorOf(l)}>
                            {l.vendor || '+ vendor'}
                          </button>
                        ) : (l.vendor || '—')}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{l.poNumber || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {l.invoiceNumber || l.invoiceDate ? <>{l.invoiceNumber || '—'}{l.invoiceDate && <span className="text-slate-500"> · {poDateText(l.invoiceDate)}</span>}</> : '—'}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{l.byName || '—'}</td>
                </tr>
              ))}
            </tbody>
            {lines.length > 0 && (
              <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                <tr>
                  <td colSpan={2} className="px-3 py-2 text-right font-semibold text-slate-600">Total</td>
                  <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{totalQty != null ? <>{fmtQ(totalQty)} {unit}</> : ''}</td>
                  <td />
                  <td className="px-3 py-2 text-right font-bold text-amber-700">₹{money(total)}</td>
                  <td colSpan={kind === 'consumption' ? 2 : 4} />
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">{cfg.note}</p>
      {vendorOf && (
        <ReceiptVendorModal line={vendorOf} onClose={() => setVendorOf(null)}
          onSaved={() => { setVendorOf(null); void qc.invalidateQueries({ queryKey: ['inv-report'] }); }} />
      )}
    </div>
  );
}

/** Who a receipt came from, set after the fact: only the vendor's name changes. */
function ReceiptVendorModal({ line, onClose, onSaved }: { line: ReportLine; onClose: () => void; onSaved: () => void }) {
  const vendors = useVendors();
  const [name, setName] = useState(line.vendor ?? '');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api(`/api/inventory/history/${line.id}/vendor`, { method: 'PUT', body: JSON.stringify({ vendorName: name.trim() || null }) }),
    onSuccess: onSaved,
    onError: (e) => setErr((e as Error).message === 'vendor_column_missing'
      ? 'The vendor cannot be saved yet — the database update for it has not been run.'
      : 'Could not save the vendor.'),
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); setErr(null); save.mutate(); }}>
        <h2 className="text-lg font-semibold mb-1 text-brand">Vendor of this receipt</h2>
        <div className="text-xs text-slate-500 mb-4">
          <b className="text-slate-700">{line.itemName}</b> · <span className="font-mono">{line.sku}</span> · {fmtQ(line.qty)}{line.uom && ` ${line.uom}`}
          <div>{dateTimeText(line.at)}{line.poNumber && <> · PO {line.poNumber}</>}{line.invoiceNumber && <> · Invoice {line.invoiceNumber}</>}</div>
        </div>
        <label className="label">Vendor</label>
        <input className="input" list="inv-receipt-vendors" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoFocus
          placeholder="Pick from the vendor master, or type" />
        <datalist id="inv-receipt-vendors">{(vendors.data ?? []).map((v) => <option key={v.id} value={v.name.trim()} />)}</datalist>
        <div className="mt-1 text-[11px] text-slate-500">Left empty, the receipt goes back to its PO's vendor (if it has one).</div>
        {err && <div className="text-red-700 text-sm mt-3">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- Earlier POs

/** A PO from the earlier PO screen (tblInvPO). */
interface LegacyPo {
  poId: number;
  poNumber: string;
  vendorName: string;
  poDate: string;
  status: string;
  expectedDate: string | null;
}

interface LegacyPoLine {
  lineId: number;
  itemCode: string;
  itemName: string;
  uom: string;
  orderQty: number;
  receivedQty: number;
  rate: number;
  description: string | null;
}

/**
 * POs raised on the earlier PO screen, before POs moved to the company PO
 * form.  Shown only when there are some; they can still be received against.
 */
function LegacyPoList({ categories }: { categories: string[] }) {
  const cats = categories.join(',');
  const { data } = useQuery({
    queryKey: ['inv-pos', cats],
    queryFn: () => api<{ rows: LegacyPo[] }>(`/api/inventory/pos?categories=${encodeURIComponent(cats)}`),
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const pos = data?.rows ?? [];
  if (!pos.length) return null;

  return (
    <div className="mt-6">
      <button type="button" className="text-sm font-medium text-slate-600 hover:text-slate-800" onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} Earlier POs, from the previous PO screen ({pos.length})
      </button>
      {open && (
        <div className="mt-2 bg-white rounded shadow overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>PO #</Th><Th>Date</Th><Th>Vendor</Th><Th>Status</Th><Th>Expected</Th></tr>
            </thead>
            <tbody>
              {pos.map((po) => (
                <tr key={po.poId} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setViewing(po.poId)}>
                  <td className="px-3 py-1.5 font-mono font-semibold">{po.poNumber}</td>
                  <td className="px-3 py-1.5">{poDateText(po.poDate)}</td>
                  <td className="px-3 py-1.5">{po.vendorName}</td>
                  <td className="px-3 py-1.5">{po.status}</td>
                  <td className="px-3 py-1.5">{po.expectedDate ? poDateText(po.expectedDate) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {viewing != null && <LegacyPoViewer id={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function LegacyPoViewer({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = can(user, 'MANAGE_INVENTORY');
  const { data, isLoading } = useQuery({
    queryKey: ['inv-po', id],
    queryFn: () => api<LegacyPo & { lines: LegacyPoLine[] }>(`/api/inventory/pos/${id}`),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {isLoading || !data ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold">PO #{data.poNumber}</h2>
                <div className="text-sm text-slate-500">{data.vendorName} • {poDateText(data.poDate)}</div>
              </div>
              <span className="px-2 py-1 rounded text-sm font-bold bg-slate-100 text-slate-800">{data.status}</span>
            </div>
            <table className="w-full text-sm mt-4">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Item</Th><Th numeric>Order Qty</Th><Th numeric>Received</Th><Th numeric>Rate</Th><Th>{' '}</Th></tr>
              </thead>
              <tbody>
                {data.lines.map((l) => (
                  <tr key={l.lineId} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium">{l.description || l.itemName}<div className="text-xs text-slate-400">{l.itemCode}</div></td>
                    <td className="px-3 py-2 text-right">{l.orderQty} {l.uom}</td>
                    <td className="px-3 py-2 text-right font-semibold text-green-700">{l.receivedQty} {l.uom}</td>
                    <td className="px-3 py-2 text-right">₹{l.rate}</td>
                    <td className="px-3 py-2 text-right">
                      {canManage && l.receivedQty < l.orderQty && (
                        <button className="text-brand hover:underline text-xs" onClick={() => {
                          const qty = prompt(`Receive how many ${l.uom} of ${l.itemCode}? (Max ${l.orderQty - l.receivedQty})`, String(l.orderQty - l.receivedQty));
                          if (!qty || isNaN(Number(qty)) || Number(qty) <= 0) return;
                          api(`/api/inventory/pos/${id}/receive`, { method: 'POST', body: JSON.stringify({ lineId: l.lineId, qty: Number(qty) }) })
                            .then(() => qc.invalidateQueries({
                              predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('inv-'),
                            }))
                            .catch((e) => alert(e.message));
                        }}>Receive</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <div className="mt-6 flex justify-end">
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
